/**
 * Engineered roads: a designed vertical profile per road, and the terrain cut
 * and filled to meet it.
 *
 * Draping a road onto the terrain vertex by vertex copies every lump of the
 * elevation data into the carriageway — and the data here is a 14 m mesh of
 * a 5 m/px model, so a motorway comes out as a washboard, tilted across
 * wherever the hillside is. Real roads are built the other way round: the
 * road gets a smooth profile within a grade its class allows, level across,
 * and the ground is dug away or banked up to carry it.
 *
 * So for every road wide enough to be engineered:
 *
 * 1. **Profile.** The ground under the centreline is smoothed over a window
 *    that grows with the road class, limited to the class's maximum grade,
 *    and kept within a class's maximum cut or fill of the real ground — a
 *    town street may move a metre, a motorway ten.
 * 2. **Junctions agree.** Roads are solved most important first; a lesser
 *    road meeting one already solved is pinned to its height there, the
 *    correction eased out over a transition length. At the tile edge a road
 *    is pinned to the raw ground, which the neighbouring tile sees too, so
 *    the two halves of a road meet.
 * 3. **Cut and fill.** Terrain vertices along the road are moved to just
 *    under its surface; the mesh triangles between them and untouched ground
 *    become the cutting or the embankment, and pick up the rock colour when
 *    steep. Because heights everywhere are read off that same grid, the
 *    player, trees, houses and every path joining the road follow the new
 *    ground without knowing it moved.
 *
 * Where it has to choose, it chooses looks: a road may leave its mapped
 * height by up to its class's cut or fill so it reads as one smooth,
 * connected surface.
 *
 * Pure: typed arrays in, typed arrays out.
 */

import type { TerrainMeshData } from './terrainmesh.js';
import { BRIDGE, TUNNEL } from './roads.js';
import { TUNNEL_ROOF, tunnelHeadroom } from './tunnels.js';

/** How a road class is engineered. */
interface ClassDesign {
  /** Smoothing window along the road, metres. */
  window: number;
  /**
   * How long a road takes to change its grade, metres: the length scale of
   * its vertical curves. A motorway rises and falls over hundreds of metres;
   * a street over a few dozen.
   */
  curve: number;
  /** Steepest grade the profile may take, rise over run. */
  grade: number;
  /** Most the road may sit above or below the ground under it, metres. */
  maxDeviation: number;
  /** How far past the kerb the ground is reshaped, in terrain cells. */
  reach: number;
}

/**
 * By `ROAD_STYLE`. Paths, footways, tracks and steps are absent: they follow
 * the ground, as they do — over the (reshaped) terrain, so a footpath joining
 * a cut road comes down into the cut with it.
 */
const DESIGN: Partial<Record<number, ClassDesign>> = {
  0: { window: 160, curve: 130, grade: 0.05, maxDeviation: 30, reach: 1.35 },   // motorway
  1: { window: 100, curve: 85, grade: 0.07, maxDeviation: 18, reach: 1.2 },     // arterial
  2: { window: 60, curve: 50, grade: 0.09, maxDeviation: 10, reach: 1.1 },      // collector
  3: { window: 30, curve: 24, grade: 0.12, maxDeviation: 4, reach: 0.9 },       // street
  4: { window: 16, curve: 12, grade: 0.16, maxDeviation: 2, reach: 0.75 },      // service
  11: { window: 180, curve: 150, grade: 0.025, maxDeviation: 25, reach: 1.25 }, // rail
  12: { window: 100, curve: 85, grade: 0.06, maxDeviation: 12, reach: 0.8 },   // tram
};

/**
 * A slip road: one lane between a motorway and the road above it. It climbs
 * what the main line never would, on a much shorter curve.
 */
const LINK_DESIGN: ClassDesign = { window: 50, curve: 45, grade: 0.07, maxDeviation: 14, reach: 1.1 };

/** A bridge of a class with no design of its own — a footbridge, a track. */
const FOOTBRIDGE_DESIGN: ClassDesign = { window: 16, curve: 12, grade: 0.12, maxDeviation: 6, reach: 0 };

/** A path leading onto a footbridge: a ramp up to the deck, on a bank. */
const PATH_RAMP_DESIGN: ClassDesign = { window: 24, curve: 18, grade: 0.1, maxDeviation: 7, reach: 0.6 };

/**
 * Most a road dips to pass under a footbridge, metres: not at all. The road
 * keeps to the ground and the footbridge arches over it (`FOOT_ARC_GRADE`).
 * Dug under one at Landvetter resecentrum, the street plunged into a pit the
 * square beside it slid down into.
 */
const FOOTBRIDGE_DIP = 0;
/** Most a road dips to pass under a more important road's bridge, metres: that road rises instead. */
const UNDER_MAJOR_DIP = 1;

/**
 * The grade a deck is planned at over what it crosses: none. A real bridge is
 * a level deck with the climb carried by its approaches on embankments; a
 * deck left to climb on its own comes out pointed.
 */
const DECK_GRADE = 0;

/** Motorway ribbons narrower than this are slip roads. See `streetHalfWidth`. */
const LINK_HALF_WIDTH = 4;

/**
 * The grade a footbridge's deck climbs at from its ends toward what it
 * crosses: it arches up from the paths' level rather than standing level on
 * the banks they ramp up. Where the arch would be steeper than this, the
 * ends rise and the paths climb the rest.
 */
const FOOT_ARC_GRADE = 0.08;

function deckGrade(run: { style: number; halfWidth: number }): number {
  const design = designFor(run.style, run.halfWidth);
  if (design === undefined) return FOOT_ARC_GRADE;
  return Math.min(DECK_GRADE, design.grade);
}

function designFor(style: number, halfWidth: number): ClassDesign | undefined {
  if (style === 0 && halfWidth < LINK_HALF_WIDTH) return LINK_DESIGN;
  return DESIGN[style];
}

/** `ROAD_STYLE.rail`. */
const RAIL = 11;
const TRAM = 12;
/** `ROAD_STYLE.street`: it and the lesser roads are held at their junctions. */
const ROAD_STYLE_STREET = 3;

/** Solve order: the road everything else must meet goes first. */
const RANK: Record<number, number> = { 0: 0, 11: 1, 12: 2, 1: 2, 2: 3, 3: 4, 4: 5 };

/**
 * Widths in the ground the roads reshape, metres — fixed, not in terrain
 * cells, so a finer grid gives the same cuttings, only better drawn. Class
 * reaches are multiples of `REACH_UNIT`. Past the kerb the ground is on the
 * road's level for `FULL_BAND`, then slopes back to the land within
 * `MAX_SLOPE`.
 */
const REACH_UNIT = 14;
const FULL_BAND = 4;
const MAX_SLOPE = 70;

/** Least drop from a kerb to the ground that gets an embankment face, metres. */
const EMBANKMENT_MIN_DROP = 0.35;

/**
 * The terrain is left this far under the road's drawn surface, beside it as
 * well as under it: a kerb's height, so the street and the ground meet
 * instead of the street riding on a strip. The road material's polygon
 * offset, not a gap, keeps the two from fighting over pixels.
 */
export const UNDER_ROAD = 0.1;
/**
 * Where a lesser road runs onto a greater one it takes its height, this far
 * under: close enough to read as one surface, far enough that the two never
 * fight over the same pixels.
 */
const UNDERLAP = 0.05;
/** As `UNDERLAP`, for a track meeting another: ballast on ballast, barely apart. */
const RAIL_UNDERLAP = 0.01;
/** Steepest a railway climbs between two of its pins before one is given up. */
const RAIL_PIN_CLIMB = 0.05;
/** Steepest a tunnel's floor dips under what crosses over it: its class's grade, a road's at least 6 %. */
function tunnelGrade(style: number, halfWidth: number): number {
  if (style === RAIL) return DESIGN[RAIL]!.grade;
  const design = designFor(style, halfWidth);
  return design !== undefined ? Math.max(design.grade, 0.06) : 0.12;
}

/**
 * On the laser ground model, by `ROAD_STYLE`: the lesser roads' smoothing
 * window and curve scaled by `scale`, their departure from the ground held to
 * `deviation` metres, and as steep as `grade`: the model has the street as
 * steep as it is. Held to a street's 12 %, one up a hill at Landvetter that
 * climbs 14.5 % was laid in a straight line over it, 7 m over the land at its
 * middle. A motorway or a railway keeps its design: its long, even grade is
 * what it is, and the model has its cuttings already.
 */
const FINE_GROUND: Partial<Record<number, { scale: number; deviation: number; grade: number }>> = {
  2: { scale: 0.6, deviation: 3, grade: 0.15 },
  3: { scale: 0.5, deviation: 1.5, grade: 0.25 },
  4: { scale: 0.5, deviation: 1, grade: 0.3 },
};

/** Steepest a path runs down to an underpass's portal, rise per metre. */
const RAMP_GRADE = 0.08;

/** A road or railway: what a path's tunnel is dug to pass under, and a way carrying traffic is not, being bridged instead. */
function carriesTraffic(style: number): boolean {
  return style <= 4 || style === RAIL;
}

/** Least ground over a tunnel's roof, away from its portals, metres. */
const TUNNEL_UNDER = 1.5;
/** How far under the ground a tunnel is held where it runs off the tile, metres: under its own roof. */
export const TUNNEL_COVER = 9;
/** How far past a railway bridge's end its approach is looked for, through short ways of track, metres. */
const RAIL_REACH = 40;
/** And past a road's deck's end, through short ways of its own class the map split off between it and the road on, metres. */
const STUB_REACH = 15;
/**
 * Deck over road, surface to surface: the clearance `bridges.ts` asks for
 * under the deck (4.8 m over a road, 6.2 m over rail) plus the deck's own
 * depth and a margin — so a designed deck already clears, and that module's
 * raising is only a safety net.
 */
const HEADROOM = 5.9;
const HEADROOM_RAIL = 7.2;
/**
 * Over a way through an underpass, a car's: the road over keeps its level and
 * the way under goes down to pass beneath it, lower than a street's headroom
 * where it must. Bridged up over a local road, a motorway humped over each of
 * the three tunnels the map has under it at Hålan. See `UNDERPASS_CLEARANCE`
 * in `bridges.ts`, which this is with the deck's depth.
 */
const HEADROOM_UNDERPASS = 3.6;
/**
 * Over a path under a road's deck, a pedestrian underpass: `clearanceOver` a
 * footway in `bridges.ts` with the deck's depth.
 */
const HEADROOM_PATH = 3.6;
/**
 * Most a path dips to pass under a road's deck, metres: a pedestrian
 * underpass is dug, the road over it stays near its land. A path has no
 * mapped level to keep; a road's slip roads have junctions to meet.
 */
const PATH_UNDER_DIP = 2.5;

export interface RoadRibbons {
  /** Flat x,z pairs, two vertices per cross-section. */
  positions: Float32Array;
  /** Two triangles per quad; quad k starts at vertex indices[6k]. */
  indices: Uint32Array;
  /** Per vertex: side, distance along the road, style, half-width. */
  attributes: Float32Array;
  /** Per vertex: 1 on a bridge deck, 2 in a tunnel (`BRIDGE`, `TUNNEL` in `roads.ts`). */
  bridges: Uint8Array;
  /** Per vertex: 1 on a way through an underpass (`tunnels.ts`). */
  underpasses?: Uint8Array;
}

/** One road's cross-sections, in order. */
interface Run {
  /** Left vertex of each cross-section; the right is the next index. */
  vertices: number[];
  style: number;
  halfWidth: number;
  bridge: boolean;
  /** A tunnel through the ground: solved last, on its own. See `solveTunnel`. */
  tunnel: boolean;
  /** A way through an underpass, under the deck it gave the road over it. */
  underpass: boolean;
  /** Centre x, z and distance along the road per cross-section. */
  cx: Float64Array;
  cz: Float64Array;
  s: Float64Array;
}

/** A stretch of designed centreline, for pinning and for reshaping ground. */
export interface CorridorSegment {
  x0: number; z0: number; h0: number;
  x1: number; z1: number; h1: number;
  halfWidth: number;
  /** `ROAD_STYLE`, so the ground can meet the drawn surface, not the centreline design. */
  style?: number;
  /** How far past the kerb the ground follows this road, metres. */
  reach: number;
  /** A bridge deck: roads join it at its ends, and pass under it elsewhere. */
  bridge?: boolean;
  /** Which run it belongs to, so a road can ignore the ones it passes over. */
  run?: number;
  /**
   * Part of a road where it dips under a bridge. Nothing joins a road at
   * grade inside the cutting under a bridge, so no other road takes its
   * height from here: the roads alongside are passing over it, or beside it.
   */
  underBridge?: boolean;
  /**
   * A staircase: the ground climbs with it however built-up the land, as
   * that climb is the whole reason the stairs are there. See `stairs.ts`.
   */
  stair?: boolean;
}

export interface RoadProfile {
  /**
   * Centreline design height per ribbon vertex (both kerbs of a section get
   * the same), NaN for vertices left to follow the ground.
   */
  design: Float32Array;
  /** Where the terrain has to be reshaped. */
  corridors: CorridorSegment[];
  /** The junctions laid level, each with the plate that fills it. See `levelJunctions`. */
  junctions: JunctionPlate[];
}

/**
 * A junction's surface: the plane every road meeting there is laid on, and
 * the outline of the asphalt plate that fills the gaps between their ends.
 */
export interface JunctionPlate {
  /** Convex outline, x, z pairs. */
  outline: number[];
  /** Height at (x, z) is `h + gx * (x - cx) + gz * (z - cz)`. */
  cx: number;
  cz: number;
  h: number;
  gx: number;
  gz: number;
  /** The biggest road's style, for the plate's surface. */
  style: number;
}

/** The ribbons' runs: consecutive quads of one road, two vertices apart. */
function runsOf(roads: RoadRibbons): Run[] {
  const runs: Run[] = [];
  const quads = Math.floor(roads.indices.length / 6);
  let start = -1;
  let previous = -1;
  const close = (first: number, last: number) => {
    const vertices: number[] = [];
    for (let a = first; a <= last + 2; a += 2) vertices.push(a);
    const n = vertices.length;
    const cx = new Float64Array(n), cz = new Float64Array(n), s = new Float64Array(n);
    vertices.forEach((a, k) => {
      cx[k] = (roads.positions[a * 2] + roads.positions[(a + 1) * 2]) / 2;
      cz[k] = (roads.positions[a * 2 + 1] + roads.positions[(a + 1) * 2 + 1]) / 2;
      s[k] = roads.attributes[a * 4 + 1];
    });
    runs.push({
      vertices,
      style: roads.attributes[first * 4 + 2],
      halfWidth: roads.attributes[first * 4 + 3],
      bridge: roads.bridges[first] === BRIDGE,
      tunnel: roads.bridges[first] === TUNNEL,
      underpass: roads.underpasses?.[first] === 1,
      cx, cz, s,
    });
  };
  for (let q = 0; q < quads; q++) {
    const a = roads.indices[q * 6];
    if (start < 0) start = a;
    else if (a !== previous + 2) {
      close(start, previous);
      start = a;
    }
    previous = a;
  }
  if (start >= 0) close(start, previous);
  return runs;
}

/**
 * The smoothest profile close to `target`: a penalised least-squares spline
 * (the Whittaker smoother) on unevenly spaced sections. It minimises
 *
 *   Σ w_k·len_k·(z_k − target_k)²  +  λ·Σ len_i·κ_i²
 *
 * where κ is the second divided difference — the profile's curvature, in
 * 1/m — so λ^¼ is the length over which the road bends: its vertical curve.
 * Sections flagged `hard` are held at their target exactly. The system is
 * symmetric, positive definite and pentadiagonal, solved in O(n).
 */
function fitProfile(target: Float64Array, s: Float64Array, w: Float64Array, hard: Uint8Array, lambda: number): Float64Array {
  const n = target.length;
  if (n < 3) return Float64Array.from(target);
  const h = new Float64Array(n - 1);
  for (let i = 0; i + 1 < n; i++) h[i] = Math.max(0.05, s[i + 1] - s[i]);
  // Banded storage: band[k][d] for A[k][k+d], d = 0..2 (symmetric).
  const A0 = new Float64Array(n), A1 = new Float64Array(n), A2 = new Float64Array(n);
  const rhs = new Float64Array(n);
  const HARD = 1e10;
  for (let k = 0; k < n; k++) {
    const len = ((k > 0 ? h[k - 1] : 0) + (k + 1 < n ? h[k] : 0)) / 2;
    const wk = hard[k] === 1 ? HARD : w[k] * len;
    A0[k] += wk;
    rhs[k] += wk * target[k];
  }
  for (let i = 1; i + 1 < n; i++) {
    const hp = h[i - 1], hn = h[i];
    const a = 2 / (hp * (hp + hn)), c = 2 / (hn * (hp + hn)), b = -(a + c);
    const m = lambda * (hp + hn) / 2;
    // Row r = [a, b, c] at columns i-1, i, i+1; A += m·rᵀr.
    const idx = [i - 1, i, i + 1], val = [a, b, c];
    for (let p = 0; p < 3; p++) {
      for (let q = p; q < 3; q++) {
        const v = m * val[p] * val[q];
        const d = q - p;
        if (d === 0) A0[idx[p]] += v;
        else if (d === 1) A1[idx[p]] += v;
        else A2[idx[p]] += v;
      }
    }
  }
  // Banded Cholesky, L with two sub-diagonals: A = L·Lᵀ.
  const L0 = new Float64Array(n), L1 = new Float64Array(n), L2 = new Float64Array(n);
  for (let k = 0; k < n; k++) {
    const l2 = k >= 2 ? A2[k - 2] / L0[k - 2] : 0;
    const l1 = k >= 1 ? (A1[k - 1] - (k >= 2 ? L1[k - 2] * l2 : 0)) / L0[k - 1] : 0;
    const d = A0[k] - l1 * l1 - l2 * l2;
    L0[k] = Math.sqrt(Math.max(d, 1e-12));
    if (k >= 1) L1[k - 1] = l1;
    if (k >= 2) L2[k - 2] = l2;
  }
  // Forward: L·y = rhs, with L[k][k-1] = L1[k-1], L[k][k-2] = L2[k-2].
  const y = new Float64Array(n);
  for (let k = 0; k < n; k++) {
    let v = rhs[k];
    if (k >= 1) v -= L1[k - 1] * y[k - 1];
    if (k >= 2) v -= L2[k - 2] * y[k - 2];
    y[k] = v / L0[k];
  }
  // Back: Lᵀ·z = y.
  const z = new Float64Array(n);
  for (let k = n - 1; k >= 0; k--) {
    let v = y[k];
    if (k + 1 < n) v -= L1[k] * z[k + 1];
    if (k + 2 < n) v -= L2[k] * z[k + 2];
    z[k] = v / L0[k];
  }
  return z;
}

function maxGrade(z: Float64Array, s: Float64Array): number {
  let g = 0;
  for (let i = 1; i < z.length; i++) g = Math.max(g, Math.abs(z[i] - z[i - 1]) / Math.max(0.05, s[i] - s[i - 1]));
  return g;
}

/** Limit the grade: a forward and a backward pass of slope clamping. */
function limitGrade(z: Float64Array, s: Float64Array, grade: number, fixed: Uint8Array): void {
  const n = z.length;
  for (let i = 1; i < n; i++) {
    if (fixed[i] === 1) continue;
    const step = grade * (s[i] - s[i - 1]);
    z[i] = Math.min(z[i - 1] + step, Math.max(z[i - 1] - step, z[i]));
  }
  for (let i = n - 2; i >= 0; i--) {
    if (fixed[i] === 1) continue;
    const step = grade * (s[i + 1] - s[i]);
    z[i] = Math.min(z[i + 1] + step, Math.max(z[i + 1] - step, z[i]));
  }
}

/** Solved centrelines, hashed for "is this point on a road already designed?". */
class SolvedIndex {
  private readonly cells = new Map<number, CorridorSegment[]>();
  private static readonly CELL = 12;

  add(seg: CorridorSegment): void {
    const c = SolvedIndex.CELL;
    const length = Math.hypot(seg.x1 - seg.x0, seg.z1 - seg.z0);
    const steps = Math.max(1, Math.ceil(length / (c / 2)));
    const keys = new Set<number>();
    for (let k = 0; k <= steps; k++) {
      const t = k / steps;
      keys.add(Math.floor((seg.x0 + (seg.x1 - seg.x0) * t) / c) * 100003 + Math.floor((seg.z0 + (seg.z1 - seg.z0) * t) / c));
    }
    for (const key of keys) {
      let list = this.cells.get(key);
      if (list === undefined) this.cells.set(key, (list = []));
      list.push(seg);
    }
  }

  /**
   * Design height of the nearest solved road whose surface comes within
   * `reach` of (x, z) past its own kerb — or undefined. `bridges` picks
   * whether decks count: a road joins a deck only at its end.
   */
  heightAt(x: number, z: number, reach: number, bridges: boolean, ignore?: ReadonlySet<number>): number | undefined {
    return this.nearest(x, z, reach, bridges, ignore)?.h;
  }

  /** As `heightAt`, with the style of the road it found. */
  nearest(x: number, z: number, reach: number, bridges: boolean, ignore?: ReadonlySet<number>, lowest = false): { h: number; style: number; run?: number } | undefined {
    const c = SolvedIndex.CELL;
    const cx = Math.floor(x / c), cz = Math.floor(z / c);
    let best: { h: number; style: number; run?: number } | undefined;
    let bestBeyond = Infinity;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        for (const seg of this.cells.get((cx + dx) * 100003 + (cz + dz)) ?? []) {
          if ((seg.bridge === true) !== bridges) continue;
          if (ignore !== undefined && seg.run !== undefined && ignore.has(seg.run)) continue;
          if (seg.underBridge === true) continue;
          const { d, t } = project(seg, x, z);
          const beyond = d - seg.halfWidth;
          const h = seg.h0 + (seg.h1 - seg.h0) * t;
          if (beyond <= reach && (lowest ? h < (best?.h ?? Infinity) : beyond < bestBeyond)) {
            bestBeyond = beyond;
            best = { h, style: seg.style ?? -1, run: seg.run };
          }
        }
      }
    }
    return best;
  }
}

function project(seg: CorridorSegment, x: number, z: number): { d: number; t: number } {
  const ux = seg.x1 - seg.x0, uz = seg.z1 - seg.z0;
  const len2 = ux * ux + uz * uz;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - seg.x0) * ux + (z - seg.z0) * uz) / len2)) : 0;
  return { d: Math.hypot(seg.x0 + ux * t - x, seg.z0 + uz * t - z), t };
}

/** Where a bridge passes over a designed road, and what each needs there. */
interface Crossing {
  bridge: number;
  under: number;
  /** Distance along each run at the crossing. */
  sBridge: number;
  sUnder: number;
  /** Section index along each run, fractional. */
  kBridge: number;
  kUnder: number;
  ground: number;
  headroom: number;
}

/** A pedestrian way: one that lies on the ground, with no design of its own. */
function isPathStyle(style: number): boolean {
  return style === 5 || style === 6 || style === 7 || style === 8;
}

/**
 * Whether a crossing of a bridge `b` over `u` is a proper one: not at the
 * bridge's end, nor at the way under's. A road that meets the bridge at its
 * end, or ends against it, touches it without passing beneath — as in
 * `bridges.ts`. Taken for a crossing, it digs a cutting where the road
 * should rise to meet the deck.
 */
function properCrossing(b: Run, u: Run, c: { ka: number; kb: number }): boolean {
  const alongBridge = at(b.s, c.ka) - b.s[0], bridgeLength = b.s[b.s.length - 1] - b.s[0];
  const alongUnder = at(u.s, c.kb) - u.s[0], underLength = u.s[u.s.length - 1] - u.s[0];
  if (alongBridge < 3 || alongBridge > bridgeLength - 3) return false;
  return alongUnder >= 1.5 && alongUnder <= underLength - 1.5;
}

/** Whether `u` passes under the bridge `b` somewhere along it, properly. */
function crossesUnder(b: Run, u: Run): boolean {
  return crossingsOf(b, u).some(c => properCrossing(b, u, c));
}

/** Proper crossings of two polylines, as fractional section indices. */
/**
 * How far either way along a tile's edge the ground is averaged for a road
 * crossing it, metres. A motorway's two carriageways cross the edge a dozen
 * metres apart and must meet it at nearly one height, or the one pinned to
 * the other fights its own pin: wider for the big roads, which stand off the
 * ground on their banks and cuttings anyway; narrow for streets, which stay
 * on it.
 */
export function edgeSpan(style: number): number {
  return style === 0 || style === 11 ? 72 : style === 1 ? 48 : 24;
}

/** Where the segment from (x0, z0), inside the tile, to (x1, z1), outside it, crosses the edge. */
function edgeCrossing(x0: number, z0: number, x1: number, z1: number, width: number, depth: number): { x: number; z: number } {
  let t = 1;
  const dx = x1 - x0, dz = z1 - z0;
  if (x1 > width && dx > 0) t = Math.min(t, (width - x0) / dx);
  if (x1 < 0 && dx < 0) t = Math.min(t, -x0 / dx);
  if (z1 > depth && dz > 0) t = Math.min(t, (depth - z0) / dz);
  if (z1 < 0 && dz < 0) t = Math.min(t, -z0 / dz);
  t = Math.max(0, t);
  return { x: x0 + dx * t, z: z0 + dz * t };
}

function crossingsOf(a: Run, b: Run): Array<{ ka: number; kb: number; x: number; z: number }> {
  const out: Array<{ ka: number; kb: number; x: number; z: number }> = [];
  for (let i = 0; i + 1 < a.cx.length; i++) {
    const ax = a.cx[i], az = a.cz[i], ux = a.cx[i + 1] - ax, uz = a.cz[i + 1] - az;
    for (let j = 0; j + 1 < b.cx.length; j++) {
      const bx = b.cx[j], bz = b.cz[j], vx = b.cx[j + 1] - bx, vz = b.cz[j + 1] - bz;
      const den = ux * vz - uz * vx;
      if (Math.abs(den) < 1e-9) continue;
      const t = ((bx - ax) * vz - (bz - az) * vx) / den;
      const u = ((bx - ax) * uz - (bz - az) * ux) / den;
      if (t >= 0 && t <= 1 && u >= 0 && u <= 1) out.push({ ka: i + t, kb: j + u, x: ax + ux * t, z: az + uz * t });
    }
  }
  return out;
}

/** A run's value at a fractional section index. */
function at(values: ArrayLike<number>, k: number): number {
  const i = Math.max(0, Math.min(values.length - 1, Math.floor(k)));
  const j = Math.min(values.length - 1, i + 1);
  return values[i] + (values[j] - values[i]) * (k - i);
}

/**
 * Design every engineered road in a tile.
 *
 * `ground` is the terrain before any reshaping — the profile is designed from
 * the land as it was, so designing twice gives the same road. `width` and
 * `depth` bound the tile: beyond them a road runs in the tile's buffer and
 * is pinned to the ground, where the neighbouring tile will agree.
 *
 * Bridges are part of the network rather than an afterthought: where a deck
 * passes over a designed road the two share the headroom — the road below
 * dips into a cutting as far as its class allows, the deck rises the rest —
 * and the roads that lead onto a deck climb to meet it along their own
 * profile, the way an interchange's approaches ramp up to the bridge.
 */
export function designRoadProfiles(
  roads: RoadRibbons,
  ground: (x: number, z: number) => number,
  width: number,
  depth: number,
  /**
   * The ground a road is pinned to where it leaves the tile. Should be
   * something the neighbouring tile computes identically — the ground
   * averaged along the shared edge — so a lump in the elevation right at the
   * crossing does not become a bump both tiles have to climb over.
   */
  edgeGround: (x: number, z: number, style: number, halfWidth: number, underpass: boolean) => number = ground,
  /**
   * The top of whatever is over a tunnel: the ground, or a river's or lake's
   * bed. A tunnel's roof stays under it. Defaults to the ground.
   */
  overTunnel: (x: number, z: number) => number = ground,
  /**
   * The ground is the laser ground model's, the road's real shape in it
   * already (`TerrainMeshData.fromOverlay`): the lesser roads follow it
   * closely, `FINE_GROUND`, rather than smoothing over the coarse models'
   * bumps. Smoothed as there, a street up a hill stood over a metre over the
   * land it was laid in, and the drives up to it met it on a step.
   */
  fineGround = false,
  /**
   * A bridge mapped as an area: its deck's height at (x, z), if (x, z) is on
   * one. Every bridge way on it is laid at that one height, level, rather
   * than each designed on its own (`bridgeareas.ts`).
   */
  deckAt: (x: number, z: number) => number | undefined = () => undefined,
  tunnelAtEdge?: (x: number, z: number, style: number) => number,
): RoadProfile {
  const design = new Float32Array(roads.positions.length / 2).fill(NaN);
  const corridors: CorridorSegment[] = [];
  const solved = new SolvedIndex();
  // Engineered roads, every bridge, and the paths that lead onto a
  // footbridge — which have to ramp up to it rather than lie on the ground.
  const every = runsOf(roads).filter(r => r.vertices.length >= 2);
  // Tunnels have no part in the network's solving: nothing on the surface
  // meets them, shares headroom with them, or reshapes ground for them.
  const runs = every.filter(r => !r.tunnel);
  const ends = (r: Run) => [[r.cx[0], r.cz[0]], [r.cx[r.cx.length - 1], r.cz[r.cz.length - 1]]];
  /** Whether a way runs on from (x, z) as a road: some other run, not a deck, ends there. */
  const roadRunsOn = (self: number, x: number, z: number) =>
    all.some((o, oi) => oi !== self && !o.bridge && ends(o).some(([ox, oz]) => Math.hypot(ox - x, oz - z) < 1.5));
  const bridgeEndPoints = runs.filter(r => r.bridge && designFor(r.style, r.halfWidth) === undefined).flatMap(ends);
  // And the portals of a path's tunnel under a road, which go down to pass
  // under it: the path runs down a ramp to each. See `solveTunnel`.
  const portalPoints = every.filter(r => r.tunnel && !carriesTraffic(r.style)).flatMap(ends);
  const approaches = new Set(runs.filter(r => !r.bridge && designFor(r.style, r.halfWidth) === undefined &&
    ends(r).some(([x, z]) => [...bridgeEndPoints, ...portalPoints].some(([bx, bz]) => Math.hypot(bx - x, bz - z) < 1.5))));
  // And the paths a road's deck crosses over: a pedestrian underpass. The
  // path dips under the deck (`PATH_UNDER_DIP`), the deck clears what is
  // left (`HEADROOM_PATH`), and both are designed together here. Lying on
  // the ground, a path under two slip roads' 20 m decks at Örgrytemotet had
  // each deck raised 2.5 m over it by `bridges.ts` after the design, and the
  // slip roads, 13 m and 25 m from the street they leave, climbed at 16 %.
  const underpaths = new Set(runs.filter(r => !r.bridge && !r.underpass && isPathStyle(r.style) && !approaches.has(r) &&
    runs.some(b => b.bridge && designFor(b.style, b.halfWidth) !== undefined && crossesUnder(b, r))));
  const all = runs.filter(r => r.bridge || approaches.has(r) || underpaths.has(r) || designFor(r.style, r.halfWidth) !== undefined);
  dbgLog(() => `designRoadProfiles: ${all.length} runs, near: ${all.filter(r => debugAt(r)).map((r) => `#${all.indexOf(r)} ${r.style}@(${r.cx[0].toFixed(1)},${r.cz[0].toFixed(1)})`).join(' ')}`);
  const cls = (r: Run): ClassDesign => {
    const d = designFor(r.style, r.halfWidth) ?? (r.bridge ? FOOTBRIDGE_DESIGN : PATH_RAMP_DESIGN);
    const fine = fineGround && !r.bridge ? FINE_GROUND[r.style] : undefined;
    return fine === undefined ? d
      : { ...d, window: d.window * fine.scale, curve: d.curve * fine.scale, maxDeviation: Math.min(d.maxDeviation, fine.deviation), grade: Math.max(d.grade, fine.grade) };
  };

  // Ground under each section: the centre and both kerbs, centre weighted.
  const raws = all.map((run) => {
    const raw = new Float64Array(run.vertices.length);
    run.vertices.forEach((a, k) => {
      const l = ground(roads.positions[a * 2], roads.positions[a * 2 + 1]);
      const r = ground(roads.positions[(a + 1) * 2], roads.positions[(a + 1) * 2 + 1]);
      raw[k] = (l + r + 2 * ground(run.cx[k], run.cz[k])) / 4;
    });
    return raw;
  });

  // Where decks pass over designed roads.
  const crossings: Crossing[] = [];
  all.forEach((b, bi) => {
    if (!b.bridge) return;
    all.forEach((u, ui) => {
      const path = underpaths.has(u);
      if (u.bridge || (!path && designFor(u.style, u.halfWidth) === undefined)) return;
      // A path under a footbridge lies as it is; only a road's deck is over it.
      if (path && designFor(b.style, b.halfWidth) === undefined) return;
      for (const c of crossingsOf(b, u)) {
        // A junction is not a crossing: a road that meets the bridge at its
        // end, or ends against it, touches it without passing beneath — as
        // in `bridges.ts`. Taken for a crossing, it digs a cutting where the
        // road should rise to meet the deck.
        if (!properCrossing(b, u, c)) continue;
        crossings.push({
          bridge: bi, under: ui, kBridge: c.ka, kUnder: c.kb,
          sBridge: at(b.s, c.ka), sUnder: at(u.s, c.kb),
          ground: ground(c.x, c.z),
          // A road's underpass under a deck takes a road's headroom: the
          // trench at Olskroksmotet carries the E6 under ten decks.
          headroom: path ? HEADROOM_PATH : u.style === 11 ? HEADROOM_RAIL : u.underpass && !carriesTraffic(u.style) ? HEADROOM_UNDERPASS : HEADROOM,
        });
      }
    });
  });
  // How far the road below dips for a bridge: as far as its cut allowance
  // lets it, up to nearly all the headroom. A motorway runs in a cutting and
  // the road over it stays near the ground — which is what keeps a
  // roundabout on a bridge at the level of the land around it.
  // Under a footbridge only a gentle sag: the path ramps up instead, and a
  // road does not plunge for a pedestrian crossing. Nor under a bigger road's
  // bridge: a motorway or main road crossing a local road is built up on an
  // embankment over it, the local road staying near the land it serves.
  /**
   * Where a way through an underpass passes under a road over it, which is
   * not bridged there but runs on over it (`tunnels.ts`): which road, where
   * along each.
   */
  const overUnderpass: Array<Array<{ over: number; kOver: number; kUnder: number; span: number }>> = all.map(() => []);
  /**
   * And on each road over one, the gap it spans there: the elevation model
   * has the ground under a bridge it took out filled in across the gap,
   * often down to the way under, and a road following it there sagged 2 m
   * into the underpass. It spans it, smoothly, as if it were not there.
   */
  const overHole: Array<Array<{ s: number; span: number }>> = all.map(() => []);
  all.forEach((u, ui) => {
    if (!u.underpass || u.bridge) return;
    const length = u.s[u.s.length - 1] - u.s[0];
    all.forEach((o, oi) => {
      if (oi === ui || o.bridge || o.tunnel || o.underpass || !carriesTraffic(o.style)) return;
      for (const c of crossingsOf(o, u)) {
        // Through it, not at its portal: a road meeting it there runs on from it.
        const along = at(u.s, c.kb) - u.s[0];
        if (along < 1.5 || along > length - 1.5) continue;
        // Along the way under, the road over takes its width over the sine.
        const i = Math.min(o.cx.length - 2, Math.floor(c.ka)), j = Math.min(u.cx.length - 2, Math.floor(c.kb));
        const ox = o.cx[i + 1] - o.cx[i], oz = o.cz[i + 1] - o.cz[i], ux = u.cx[j + 1] - u.cx[j], uz = u.cz[j + 1] - u.cz[j];
        const sin = Math.abs(ox * uz - oz * ux) / ((Math.hypot(ox, oz) * Math.hypot(ux, uz)) || 1);
        overUnderpass[ui].push({ over: oi, kOver: c.ka, kUnder: c.kb, span: (o.halfWidth + 1) / Math.max(0.3, sin) + 2 });
        overHole[oi].push({ s: at(o.s, c.ka), span: (u.halfWidth + 3) / Math.max(0.3, sin) + 2 });
      }
    });
  });
  // Where each designed way crosses the tile's edge, from inside to beyond:
  // how far along it, and where.
  const inTile = (x: number, z: number) => x >= 0 && z >= 0 && x <= width && z <= depth;
  const edgeCrossings = all.map((r) => {
    const out: Array<{ s: number; x: number; z: number }> = [];
    if (r.bridge) return out;
    for (let k = 0; k + 1 < r.cx.length; k++) {
      const a = inTile(r.cx[k], r.cz[k]), b = inTile(r.cx[k + 1], r.cz[k + 1]);
      if (a === b) continue;
      const [i, o] = a ? [k, k + 1] : [k + 1, k];
      const e = edgeCrossing(r.cx[i], r.cz[i], r.cx[o], r.cz[o], width, depth);
      out.push({ s: r.s[i] + Math.hypot(e.x - r.cx[i], e.z - r.cz[i]) * Math.sign(r.s[o] - r.s[i]), x: e.x, z: e.z });
    }
    return out;
  });
  /**
   * The level a way through an underpass is held at where it crosses the
   * tile's edge: under a road over it crossing the edge beside it, a
   * street's full headroom under it, as far as `ARCH_DEEPEST` under the
   * ground there; else the ground. Both tiles hold the edge's ground and both
   * roads there, so both pin it alike, and each lays its half of the arch
   * level into it. At the ground, which under a bridge the elevation model
   * has filled in high across the gap, the way under the Rv 40 at Hålan
   * crested 2 m between the tunnels either side of the edge.
   */
  const underpassEdge = (ri: number, x: number, z: number): number => {
    const run = all[ri];
    const land = edgeGround(x, z, run.style, run.halfWidth, true);
    let h = land;
    all.forEach((o, oi) => {
      if (oi === ri || o.underpass || o.tunnel || o.bridge || !carriesTraffic(o.style)) return;
      for (const e of edgeCrossings[oi]) {
        if (Math.hypot(e.x - x, e.z - z) > run.halfWidth + o.halfWidth + 4) continue;
        h = Math.min(h, Math.max(edgeGround(e.x, e.z, o.style, o.halfWidth, false) - HEADROOM, land - ARCH_DEEPEST));
      }
    });
    return h;
  };
  /**
   * The lowest a way through an underpass can be at a section, going down at
   * its grade from where it is held at the tile's edge, a terrain cell in
   * (see the ceilings in `solveRoad`): -Infinity where it leaves no edge.
   */
  const underpassFloor = (ri: number, s: number): number => {
    const run = all[ri];
    let floor = -Infinity;
    if (!run.underpass) return floor;
    for (const e of edgeCrossings[ri]) {
      floor = Math.max(floor, underpassEdge(ri, e.x, e.z) - cls(run).grade * Math.max(0, Math.abs(s - e.s) - EDGE_CELL));
    }
    return floor;
  };
  const dip = (c: Crossing) => {
    const over = all[c.bridge];
    const foot = designFor(over.style, over.halfWidth) === undefined;
    // Nor a railway, not at all: it keeps its level and the road bridges it.
    // Cut 7 m under a road bridge at its 2.5 %, a short way of track in a
    // yard dived at 30 % to meet the ceiling, metres under its neighbours;
    // cut even 1 m under a motorway over it, the track was held under the
    // way it runs on into, a step.
    if (all[c.under].style === RAIL) return 0;
    // A tram on its own right of way is a local line too, whatever crosses
    // it: ranked over a slip road, the tramway at Örgryte dug 5.6 m under
    // one, a canyon 250 m long with the stop in it, and the tram's bridge
    // over the river at its end climbing out at 22 %.
    // And a slip road's deck is the motorway's: narrowed to a link it ranks
    // under a collector at a junction, but what it bridges stays at the land
    // all the same. Örgrytevägen was dug 2.4 m under the slip roads' decks at
    // Örgrytemotet, and the ramp joining it there climbed out at 22 %.
    // And a slip road under a deck is in the motorway's corridor, dug with
    // it: held up like a street under Redbergsvägen's bridge at Olskroksmotet,
    // the bridge rose 3 m over the tram's deck beside it and ended on a step.
    const under = all[c.under];
    const overRank = over.style === 0 ? RANK[0] : rankOf(over);
    const underRank = under.style === 0 ? RANK[0] : rankOf(under);
    const minor = under.style === TRAM || overRank < underRank;
    // No deeper than the deck needs, at the land its ends stand on: where
    // the elevation model has the cutting already, as the laser ground model
    // does, the motorway in it was cut 4.6 m deeper again, 9.7 m under a
    // bridge that crosses it level.
    const ends = raws[c.bridge], m = ends.length - 1, b = all[c.bridge];
    const t = b.s[m] > b.s[0] ? (c.sBridge - b.s[0]) / (b.s[m] - b.s[0]) : 0.5;
    const needed = Math.max(0, c.ground + c.headroom - (ends[0] + (ends[m] - ends[0]) * t));
    // But a way through an underpass goes down under whatever is over it, as
    // far as it must: the road over it is level.
    const most = Math.min(cls(all[c.under]).maxDeviation * 0.8,
      foot ? FOOTBRIDGE_DIP : all[c.under].underpass ? c.headroom : isPathStyle(all[c.under].style) ? PATH_UNDER_DIP : minor ? UNDER_MAJOR_DIP : c.headroom - 0.3);
    // And never under where it can come down to from the tile's edge.
    return Math.min(Math.max(deeper.get(c) ?? 0, Math.min(most, needed)), Math.max(0, c.ground - underpassFloor(c.under, c.sUnder)));
  };
  /**
   * Crossings where the road under goes down further than it would, so that
   * a deck can come down to what its approach can reach. See `plannedEnd`.
   */
  const deeper = new Map<Crossing, number>();
  const deck = new Map<number, Float64Array>();
  const heights = new Map<number, Float64Array>();
  /** For each corridor segment: its run and section, to bring it to the junctions' heights after. */
  const corridorAt: Array<[number, number]> = [];

  /** A bridge on a bridge mapped as an area: its deck's height, there in its middle. */
  const areaDeck = (bi: number): number | undefined => {
    const b = all[bi];
    if (!b.bridge) return undefined;
    const m = b.cx.length >> 1;
    return deckAt(b.cx[m], b.cz[m]);
  };
  /** What a deck will need at its ends, before it is solved: a climb at its grade to its crossings. */
  const plannedEnd = (bi: number, end: 0 | 1): number => {
    const area = areaDeck(bi);
    if (area !== undefined) return area;
    const b = all[bi];
    const n = b.vertices.length;
    const k = end === 0 ? 0 : n - 1;
    let h = raws[bi][k];
    for (const c of crossings) {
      if (c.bridge !== bi) continue;
      const under = heights.get(c.under);
      const need = (under !== undefined ? at(under, c.kUnder) : c.ground - dip(c)) + c.headroom;
      h = Math.max(h, need - deckGrade(b) * Math.abs(b.s[k] - c.sBridge));
    }
    // Level, unless the road onto this end cannot climb that high: held a few
    // tens of metres off at a junction or the tile's edge, it stayed on the
    // ground and the deck's end stood in the air over it. Then the deck
    // comes down toward it at its own grade, as far as its crossings let it.
    const reach = approachReach(bi, k);
    if (h <= reach) return h;
    let sloped = raws[bi][k];
    for (const c of crossings) {
      if (c.bridge !== bi) continue;
      const under = heights.get(c.under);
      const need = (under !== undefined ? at(under, c.kUnder) : c.ground - dip(c)) + c.headroom;
      sloped = Math.max(sloped, need - cls(b).grade * Math.abs(b.s[k] - c.sBridge));
    }
    return Math.max(sloped, reach);
  };
  /**
   * How high the roads leading onto a deck's end can bring it, climbing at
   * their own grade from where they are held: the next junction along them,
   * or the tile's edge. Ways the map merely split are followed on through.
   * Infinity for a road free to rise, a dead end.
   */
  const reaches = new Map<number, number>();
  const approachReach = (bi: number, k: number): number => {
    const key = bi * 2 + (k === 0 ? 0 : 1);
    const known = reaches.get(key);
    if (known !== undefined) return known;
    const b = all[bi];
    let reach = Infinity;
    for (let ri = 0; ri < all.length; ri++) {
      const r = all[ri];
      if (r.bridge) continue;
      const m = r.cx.length - 1;
      const atStart = Math.hypot(r.cx[0] - b.cx[k], r.cz[0] - b.cz[k]) <= b.halfWidth + r.halfWidth;
      if (!atStart && Math.hypot(r.cx[m] - b.cx[k], r.cz[m] - b.cz[k]) > b.halfWidth + r.halfWidth) continue;
      let length = 0, cur = ri, far = atStart ? m : 0;
      const seen = new Set<number>([bi]);
      for (let step = 0; step < 4; step++) {
        const c = all[cur];
        seen.add(cur);
        length += c.s[c.s.length - 1] - c.s[0];
        const x = c.cx[far], z = c.cz[far];
        if (x < 0 || z < 0 || x > width || z > depth) { reach = Math.min(reach, raws[cur][far] + cls(r).grade * length); break; }
        // What else is there: a way running on, or a junction holding it.
        const others = all.map((o, oi) => oi).filter(oi => !seen.has(oi) &&
          all[oi].cx.some((ox, ok) => Math.hypot(ox - x, all[oi].cz[ok] - z) <= c.halfWidth + all[oi].halfWidth));
        if (others.length === 0) break;
        const on = others.length === 1 ? all[others[0]] : undefined;
        const onEnd = on === undefined || on.bridge ? -1
          : Math.hypot(on.cx[0] - x, on.cz[0] - z) < 1.5 ? 0 : Math.hypot(on.cx[on.cx.length - 1] - x, on.cz[on.cx.length - 1] - z) < 1.5 ? on.cx.length - 1 : -1;
        // A junction holds a street there, at the ground; a motorway or a main
        // road runs on through it on its own profile, on its banks, and only
        // the tile's edge holds it. Held at a slip road's merge, a motorway's
        // deck over a railway came down to meet it in a bump.
        if (onEnd < 0 || step === 3) {
          if (r.style >= ROAD_STYLE_STREET) reach = Math.min(reach, raws[cur][far] + cls(r).grade * length);
          break;
        }
        cur = others[0];
        far = onEnd === 0 ? all[cur].cx.length - 1 : 0;
      }
    }
    reaches.set(key, reach);
    return reach;
  };
  /**
   * The roads a run passes over: those under its own deck, if it is a bridge,
   * or under a bridge it leads onto. Near its deck a road runs right beside
   * the road below, and must not take that road's height for its own —
   * snapping a bridge end down into the cutting it spans is a spike.
   */
  const passesOver = all.map(() => new Set<number>());
  /**
   * Per run, whether each end runs on from a way under a deck: the road on
   * out of an underpass's portal, which is right beside the deck over it
   * but leads onto it no more than the way under does. Taken for its
   * approach, it climbed from the underpass's floor onto the deck in one
   * step, 6 m, at the portal. It is at the way under's level there, and
   * nothing else's: not the deck's, nor the road's on beside the deck.
   */
  const unders = [...new Set(crossings.map(c => c.under))];
  const runsOnUnder = all.map((r, ri) => ends(r).map(([x, z]) => {
    if (r.bridge) return undefined;
    for (const ui of unders) {
      if (ui === ri) continue;
      const u = all[ui], last = u.cx.length - 1;
      if (Math.hypot(u.cx[0] - x, u.cz[0] - z) < 1) return { ui, uk: 0 };
      if (Math.hypot(u.cx[last] - x, u.cz[last] - z) < 1) return { ui, uk: last };
    }
    return undefined;
  }));
  all.forEach((r, ri) => {
    const own = crossings.filter(c => c.bridge === ri).map(c => c.under);
    for (const u of own) passesOver[ri].add(u);
    // A way through an underpass passes under the roads over it, not onto them.
    for (const c of overUnderpass[ri]) passesOver[ri].add(c.over);
    if (r.bridge) return;
    const endsOf = ends(r).filter((_, e) => runsOnUnder[ri][e] === undefined);
    all.forEach((b, bi) => {
      if (!b.bridge) return;
      const bEnds = [[b.cx[0], b.cz[0]], [b.cx[b.cx.length - 1], b.cz[b.cz.length - 1]]];
      const joins = endsOf.some(([x, z]) => bEnds.some(([bx, bz]) => Math.hypot(bx - x, bz - z) <= b.halfWidth + r.halfWidth));
      if (joins) for (const c of crossings) if (c.bridge === bi) passesOver[ri].add(c.under);
    });
  });

  /** Bridge ends, for the approaches that lead onto them. */
  const bridgeEnds: Array<{ bi: number; end: 0 | 1; x: number; z: number; hw: number; along: number }> = all.flatMap((b, bi) => b.bridge
    ? [0, b.vertices.length - 1].map(k => ({ bi, end: (k === 0 ? 0 : 1) as 0 | 1, x: b.cx[k], z: b.cz[k], hw: b.halfWidth, along: 0 }))
    : []);
  // A railway bridge's end carried on through the short ways of track the
  // map has between it and the line, so the line climbs to it on its own
  // grade. Else the line, solved first, stayed on the ground, and the few
  // metres of track between climbed metres. A road's deck's end likewise
  // through a short way of its own class: at Örgrytemotet a slip road's
  // deck ends on a 6 m stub to the ramp down to the street, solved before
  // it as the longer; the ramp came down from the ground where the stub
  // ends, and the stub, unable to climb a metre in 6 m, gave the deck up
  // and stepped off its end.
  for (let i = 0; i < bridgeEnds.length; i++) {
    const e = bridgeEnds[i];
    const b = all[e.bi];
    const most = b.style === RAIL ? RAIL_REACH : STUB_REACH;
    if (e.along > most) continue;
    for (const r of all) {
      if (r.bridge || r.style !== b.style) continue;
      const m = r.cx.length - 1, length = r.s[m] - r.s[0];
      if (e.along + length > most) continue;
      for (const [k, far] of [[0, m], [m, 0]]) {
        if (Math.hypot(r.cx[k] - e.x, r.cz[k] - e.z) > 1.5) continue;
        if (bridgeEnds.some(o => o.bi === e.bi && Math.hypot(o.x - r.cx[far], o.z - r.cz[far]) < 1.5)) continue;
        bridgeEnds.push({ ...e, x: r.cx[far], z: r.cz[far], hw: Math.min(e.hw, r.halfWidth), along: e.along + length });
      }
    }
  }

  // Most important first; among equals the longest, which the others join.
  const order = all.map((_, i) => i);
  const length = (r: Run) => r.s[r.s.length - 1] - r.s[0];
  // A class's decks before its roads: planned at the height they need, the
  // roads onto them climb to meet them on their own profile. Solved after a
  // long approach that came down to it lower, a motorway's deck over a
  // railway at Landvetter had to make up the difference itself: a bump.
  const deckFirst = (r: Run) => (r.bridge && designFor(r.style, r.halfWidth) !== undefined ? 0 : 1);
  order.sort((a, b) => (rankOf(all[a]) - rankOf(all[b])) || (deckFirst(all[a]) - deckFirst(all[b])) || (length(all[b]) - length(all[a])));
  // A way through an underpass right after the roads over it, to go down
  // under them, and before the roads on out of it, to meet it there.
  for (let ui = 0; ui < all.length; ui++) {
    if (!all[ui].underpass || all[ui].bridge) continue;
    if (overUnderpass[ui].length > 0) {
      order.splice(order.indexOf(ui), 1);
      const after = Math.max(...overUnderpass[ui].map(c => order.indexOf(c.over)));
      order.splice(after + 1, 0, ui);
    }
    // The roads on out of its portals after it, whatever their length: the
    // E6 running on into the trench's way at Olskroksmotet, solved first as
    // the longer, ended 4 m over the way's level, and the way was held to it.
    const portals = ends(all[ui]);
    for (const oi of [...order]) {
      const o = all[oi];
      if (oi === ui || o.bridge || o.underpass || order.indexOf(oi) > order.indexOf(ui)) continue;
      if (!ends(o).some(([x, z]) => portals.some(([px, pz]) => Math.hypot(px - x, pz - z) < 1.5))) continue;
      order.splice(order.indexOf(oi), 1);
      order.splice(order.indexOf(ui) + 1, 0, oi);
    }
  }
  /** Whether two decks run within `SIDE_DECK_COS` of parallel, end to end. */
  const parallelDecks = (ai: number, bi: number): boolean => {
    const a = all[ai], b = all[bi];
    const ax = a.cx[a.cx.length - 1] - a.cx[0], az = a.cz[a.cz.length - 1] - a.cz[0];
    const bx = b.cx[b.cx.length - 1] - b.cx[0], bz = b.cz[b.cz.length - 1] - b.cz[0];
    return Math.abs(ax * bx + az * bz) / ((Math.hypot(ax, az) * Math.hypot(bx, bz)) || 1) >= SIDE_DECK_COS;
  };
  // A deck ending on another deck's side, a slip road's merging onto a
  // flyover halfway along it, after that deck: its end is laid on the
  // flyover (`solveDeck`), whose level its own crossings know nothing of.
  // Not a deck whose way runs on past that end as a road: it passes under
  // the flyover, not onto it. The slip roads' decks over the trench at
  // Olskroksmotet end under the freight railway's viaduct, and were laid
  // on it, 7 m up.
  for (const ai of [...order]) {
    if (!all[ai].bridge) continue;
    for (const [x, z] of ends(all[ai])) {
      if (roadRunsOn(ai, x, z)) continue;
      // Nor a parallel deck of the same bridge, a tramway's beside a road's:
      // alongside, not merging. Taken for merging onto the footway's deck
      // beside it, a tram's deck at Olskroksmotet was solved after its own
      // approach, which then stood 3 m off it.
      const bi = all.findIndex((b, i) => i !== ai && b.bridge && onSideOf(b, x, z) && !parallelDecks(ai, i));
      if (bi < 0 || order.indexOf(bi) < order.indexOf(ai)) continue;
      order.splice(order.indexOf(ai), 1);
      order.splice(order.indexOf(bi) + 1, 0, ai);
    }
  }
  // Decks side by side are one structure: a road's and a tramway's over
  // the same cut, a divided road's two. The higher of what each needs is
  // solved first, and the other takes its level (`solveDeck`). Planned
  // each from its own crossings, the tram's deck beside Redbergsvägen's at
  // Olskroksmotet stood 3 m under the road's, the street along the tram was
  // held down with it, and the road's deck ended on a 2.9 m step.
  const besideDeck = (ai: number, bi: number): boolean => {
    const a = all[ai], b = all[bi];
    if (ai === bi || !a.bridge || !b.bridge) return false;
    // One structure: a tramway's deck beside a road's, or two of one kind
    // and width. A slip road's deck beside the E20's over the trench at
    // Olskroksmotet is its own, lower, and was lifted 2.75 m onto nothing.
    if (a.style !== TRAM && b.style !== TRAM && (a.style !== b.style || Math.abs(a.halfWidth - b.halfWidth) > 0.5)) return false;
    const ax = a.cx[a.cx.length - 1] - a.cx[0], az = a.cz[a.cz.length - 1] - a.cz[0];
    const bx = b.cx[b.cx.length - 1] - b.cx[0], bz = b.cz[b.cz.length - 1] - b.cz[0];
    const cos = Math.abs(ax * bx + az * bz) / ((Math.hypot(ax, az) * Math.hypot(bx, bz)) || 1);
    if (cos < SIDE_DECK_COS) return false;
    const reach = a.halfWidth + b.halfWidth + SIDE_DECK_GAP;
    let near = 0;
    for (let k = 0; k < a.cx.length; k++) if (distanceToRun(b, a.cx[k], a.cz[k]) <= reach) near++;
    return near >= Math.max(2, a.cx.length / 2);
  };
  const needOf = (bi: number) => Math.max(plannedEnd(bi, 0), plannedEnd(bi, 1));
  for (const ai of [...order]) {
    if (!all[ai].bridge) continue;
    for (const bi of [...order]) {
      if (!besideDeck(ai, bi) || order.indexOf(bi) < order.indexOf(ai)) continue;
      if (needOf(bi) > needOf(ai) + 0.05) {
        order.splice(order.indexOf(bi), 1);
        order.splice(order.indexOf(ai), 0, bi);
      }
    }
  }
  const rings = findRings(all);
  const ringOf = new Map<number, number>();
  rings.forEach((ring, i) => { for (const r of ring) ringOf.set(r, i); });
  const done = new Set<number>();

  const commit = (ri: number, z: Float64Array) => {
    const run = all[ri];
    const c = cls(run);
    const n = run.vertices.length;
    done.add(ri);
    heights.set(ri, z);
    if (run.bridge) deck.set(ri, z);

    for (let k = 0; k < n; k++) {
      const a = run.vertices[k];
      design[a] = z[k];
      design[a + 1] = z[k];
    }
    for (let k = 0; k + 1 < n; k++) {
      const seg: CorridorSegment = {
        x0: run.cx[k], z0: run.cz[k], h0: z[k],
        x1: run.cx[k + 1], z1: run.cz[k + 1], h1: z[k + 1],
        halfWidth: run.halfWidth, style: run.style, reach: c.reach, run: ri,
      };
      if (run.bridge) seg.bridge = true;
      const mid = (run.s[k] + run.s[k + 1]) / 2;
      if (crossings.some(cr => cr.under === ri && Math.abs(mid - cr.sUnder) <= all[cr.bridge].halfWidth + 15)) {
        seg.underBridge = true;
      }
      else { corridors.push(seg); corridorAt.push([ri, k]); }
      solved.add(seg);
    }
  };

  let ringsSolved = false;
  // Before anything is solved: where a deck's end cannot be reached at the
  // level its crossings would hold it, the road under it is cut deeper, as
  // far as its design lets it leave the ground, so the deck can come down to
  // its approach at its grade. A motorway in a cutting under a street bridge
  // whose end is a few metres from a junction: held at its usual dip under
  // the land, the Rv 40 by Konstruktionsvägen near Lerum kept the deck's end
  // 5 m over the junction 8 m off, on a bank. The road under is solved first,
  // under the ground less this dip, and the deck then down to meet it.
  all.forEach((b, bi) => {
    if (!b.bridge) return;
    for (const k of [0, b.vertices.length - 1]) {
      const reach = approachReach(bi, k);
      if (!Number.isFinite(reach)) continue;
      for (const c of crossings) {
        if (c.bridge !== bi || all[c.under].style === RAIL || designFor(all[c.under].style, all[c.under].halfWidth) === undefined) continue;
        // Never for a footbridge: its paths and steps climb, the road stays.
        if (designFor(b.style, b.halfWidth) === undefined) continue;
        const level = reach + cls(b).grade * Math.abs(b.s[k] - c.sBridge) - c.headroom;
        const most = cls(all[c.under]).maxDeviation * 0.8;
        if (c.ground - level > dip(c)) deeper.set(c, Math.min(most, c.ground - level));
      }
    }
  });
  for (const ri of order) {
    if (done.has(ri)) continue;
    if (debugAt(all[ri])) dbgLog(() => `order: #${ri} at ${order.indexOf(ri)} rank ${rankOf(all[ri])} deckFirst ${deckFirst(all[ri])} bridge ${all[ri].bridge} style ${all[ri].style}`);
    // Roundabouts right after the motorways and railways they bridge, and
    // before every road that joins them — so those roads meet the ring.
    if (!ringsSolved && rankOf(all[ri]) > 1) {
      ringsSolved = true;
      rings.forEach((members) => {
        if (members.some(m => done.has(m))) return;
        const level = ringLevel(members);
        for (const member of members) commit(member, new Float64Array(all[member].vertices.length).fill(level));
      });
      if (done.has(ri)) continue;
    }
    const ring = ringOf.get(ri);
    if (ring !== undefined) {
      const level = ringLevel(rings[ring]);
      for (const member of rings[ring]) commit(member, new Float64Array(all[member].vertices.length).fill(level));
      continue;
    }
    commit(ri, all[ri].bridge ? solveDeck(ri) : solveRoad(ri));
  }

  /**
   * One level for a whole roundabout: the ground it sits on, or what its
   * bridges need to clear the roads under them, whichever is higher.
   */
  function ringLevel(ring: number[]): number {
    let sum = 0, count = 0;
    for (const ri of ring) for (const h of raws[ri]) { sum += h; count++; }
    let level = sum / Math.max(1, count);
    for (const cr of crossings) {
      if (!ring.includes(cr.bridge)) continue;
      const under = heights.get(cr.under);
      level = Math.max(level, (under !== undefined ? at(under, cr.kUnder) : cr.ground - dip(cr)) + cr.headroom);
    }
    return level;
  }

  /** The height of a solved deck's end within 1.5 m of (x, z), the nearest, if any. */
  function deckEndAt(x: number, z: number): number | undefined {
    let best: number | undefined, bestD = 1.5;
    for (const other of done) {
      const o = all[other], oz = heights.get(other);
      if (!o.bridge || oz === undefined) continue;
      for (const k of [0, o.cx.length - 1]) {
        const d = Math.hypot(o.cx[k] - x, o.cz[k] - z);
        if (d < bestD) { bestD = d; best = oz[k]; }
      }
    }
    return best;
  }

  /** The height of a solved railway's end within 2 m of (x, z), the nearest, if any. */
  function trackEndAt(x: number, z: number, self: number): number | undefined {
    let best: number | undefined, bestD = 2;
    for (const other of done) {
      const o = all[other], oz = heights.get(other);
      if (other === self || o.style !== RAIL || o.bridge || oz === undefined) continue;
      for (const k of [0, o.cx.length - 1]) {
        const d = Math.hypot(o.cx[k] - x, o.cz[k] - z);
        if (d < bestD) { bestD = d; best = oz[k]; }
      }
    }
    return best;
  }

  /** The lowest ceiling the decks over a way under them hold its section `k` to: Infinity where none does. */
  function underCeiling(ui: number, k: number): number {
    const u = all[ui];
    const plateaus: Array<{ s: number; span: number; h: number }> = [];
    for (const cr of crossings) {
      if (cr.under !== ui || u.style === RAIL || designFor(all[cr.bridge].style, all[cr.bridge].halfWidth) === undefined) continue;
      const over = deck.get(cr.bridge);
      plateaus.push({ s: cr.sUnder, span: all[cr.bridge].halfWidth + 8, h: over !== undefined ? at(over, cr.kBridge) - cr.headroom : cr.ground - dip(cr) });
    }
    let limit = Infinity;
    for (const p of plateaus) if (Math.abs(u.s[k] - p.s) <= p.span) limit = Math.min(limit, p.h);
    // Between two decks, up from each at the steeper of the class's grade
    // and `CEILING_CLIMB`: a road cannot climb out of one faster to get under
    // the next. Held only under each deck's own span, the E6 northbound at
    // Olskroksmotet, under two decks 15 m apart whose undersides differ by
    // 5 m, was told to climb the 5 m between them in 10 m.
    // From the first deck's span to the last's, under every deck's line;
    // past the outermost, nothing: pinned along such a line out there, the
    // dip's smooth exit became a ramp.
    if (plateaus.length < 2) return limit;
    const climb = Math.max(cls(u).grade, CEILING_CLIMB);
    const first = Math.min(...plateaus.map(p => p.s - p.span)), last = Math.max(...plateaus.map(p => p.s + p.span));
    if (u.s[k] < first || u.s[k] > last) return limit;
    for (const p of plateaus) limit = Math.min(limit, p.h + climb * Math.max(0, Math.abs(u.s[k] - p.s) - p.span));
    return limit;
  }


  function solveRoad(ri: number): Float64Array {
    const run = all[ri];
    const c = cls(run);
    const n = run.vertices.length;
    const raw = raws[ri];

    // Pins: past the tile edge, onto roads already solved, onto the ends of
    // decks. Ceilings: under the decks that cross this road.
    const pinned = new Uint8Array(n);
    const pinValue = new Float64Array(n);
    const ceiling = new Float64Array(n).fill(Infinity);
    // Pins onto the surface of a road it joins: those it must meet.
    const joins = new Uint8Array(n);
    // Pins onto another track, for a railway, and of those its ends on the
    // end of the track it runs on from.
    const track = new Uint8Array(n);
    const runsOn = new Uint8Array(n);
    // Pins at the tile's edge, whose ground there nothing presses down: see the ceilings.
    const onEdge = new Uint8Array(n);
    // Beyond the tile's edge, unless only just, along it: a street running
    // along the edge, its centreline a metre over, was pinned flat to the
    // edge's ground by the tile it had left and designed up to the road it
    // joins by the one it was in. Each tile draws the half of it on its side,
    // so the two halves stood half a metre apart. Designed by both, they meet.
    const outside = (k: number) => {
      const x = run.cx[k], z = run.cz[k];
      const over = Math.max(-x, -z, x - width, z - depth);
      // A way's end on the edge, just short of it, runs on in the neighbour,
      // and is cut there at the edge's height (`clipToTile`): held to it, or
      // a deck by the edge at Hålan ended on a step 1.4 m down to the cut.
      if ((k === 0 || k === n - 1) && over > -END_ON_EDGE) return true;
      if (over <= 0) return false;
      if (over > run.halfWidth + ALONG_EDGE_SLACK) return true;
      const a = Math.max(0, k - 1), b = Math.min(n - 1, k + 1);
      const dx = run.cx[b] - run.cx[a], dz = run.cz[b] - run.cz[a], len = Math.hypot(dx, dz);
      if (len < 1e-6) return true;
      // Across the edge it is beyond: x for the west and east edges, z for the others.
      const across = x < 0 || x > width ? Math.abs(dx) / len : Math.abs(dz) / len;
      return across > ALONG_EDGE_SIN;
    };
    for (let k = 0; k < n; k++) {
      const x = run.cx[k], zz = run.cz[k];
      if (outside(k)) {
        pinned[k] = 1;
        // At the ground where the centreline crosses the edge — the height the
        // neighbour pins it to, and the cut is laid at (see `clipToTile`).
        // Taken where this section happens to lie past the edge instead, it
        // differed from the cut's, and the last few metres to the edge
        // climbed or dropped to meet it: a hump at every tile seam.
        // Every section out there, not only the first: pinned where each
        // happened to lie along the edge, a few sections out gave the road a
        // grade of the edge's own slope, which a railway's stiff profile then
        // carried on for hundreds of metres into the ground.
        let a = k, b = k;
        while (a > 0 && outside(a - 1)) a--;
        while (b + 1 < n && outside(b + 1)) b++;
        const [j, o] = a > 0 && (b + 1 >= n || k - a <= b - k) ? [a - 1, a] : b + 1 < n ? [b + 1, b] : [-1, k];
        const cross = j < 0 ? undefined : edgeCrossing(run.cx[j], run.cz[j], run.cx[o], run.cz[o], width, depth);
        const at = cross ?? { x, z: zz };
        pinValue[k] = run.underpass ? underpassEdge(ri, at.x, at.z) : edgeGround(at.x, at.z, run.style, run.halfWidth, false);
        onEdge[k] = 1;
        continue;
      }
      // Touching a solved road's surface: share it, just under. Alongside
      // counts too, up to half a metre off the kerb: the other carriageway of
      // a divided road, whose ribbon now meets this one kerb to kerb rather
      // than overlapping it, must be at the same level or the join is a step.
      // A track's end on the end of one it runs on from: that end's level.
      if (run.style === RAIL && (k === 0 || k === n - 1)) {
        const end = trackEndAt(x, zz, ri);
        if (end !== undefined) { pinned[k] = 1; pinValue[k] = end - RAIL_UNDERLAP; joins[k] = 1; track[k] = 1; runsOn[k] = 1; continue; }
      }
      const from = k === 0 ? runsOnUnder[ri][0] : k === n - 1 ? runsOnUnder[ri][1] : undefined;
      if (from !== undefined) {
        const h = heights.get(from.ui);
        if (h !== undefined) { pinned[k] = 1; pinValue[k] = h[from.uk] - UNDERLAP; joins[k] = 1; }
        else {
          // Not solved yet: where its end is held under the deck, at that
          // ceiling, which it will be at too (see the ceilings below).
          const limit = underCeiling(from.ui, from.uk);
          if (limit < raw[k]) { pinned[k] = 1; pinValue[k] = limit; }
        }
        continue;
      }
      // An end on a deck's end: climb to it, solved or planned, before any
      // road alongside. Pinned to the parallel carriageway's surface instead,
      // the road onto a deck at Örgrytemotet ran on from it half a metre up,
      // and a stub off the other end half a metre down.
      // A track runs on onto its bridge as onto any other way of it.
      // Not a way through an underpass: its end under a deck's kerb is not
      // on the deck. The link tunnel into the trench at Olskroksmotet ends
      // under the E20's deck, and was pinned onto it, 6 m up.
      // On a deck's end itself, at that end's height; or on a deck's side
      // where the way ends, merging onto a flyover. Not where the way runs
      // on as a road: it passes under. Taken from the nearest deck, the
      // slip road on from its deck under the freight viaduct at
      // Olskroksmotet was pinned onto the viaduct, 7 m up.
      if ((k === 0 || k === n - 1) && !run.underpass) {
        const onDeck = deckEndAt(x, zz) ?? (roadRunsOn(ri, x, zz) ? undefined : solved.heightAt(x, zz, 1.5, true));
        if (onDeck !== undefined) {
          // At it, not 5 cm under: the two butt end to end rather than overlap,
          // and any step showed the ground through a crack across the road at
          // every deck's end.
          pinned[k] = 1; pinValue[k] = onDeck;
          if (run.style === RAIL) runsOn[k] = 1;
          continue;
        }
      }
      const near = solved.nearest(x, zz, run.halfWidth + 0.5, false, passesOver[ri]);
      // A way through an underpass runs between its trough's walls: the
      // roads beside it are up on the land, and it meets only the ways on
      // out of its portals and the ways under with it. Pinned to the slip
      // roads along the trench's edge at Olskroksmotet, the sunken E6 was
      // held 3 m up between two of its ceilings.
      // Nor does a road on the land take the way under's level through
      // those walls: the E20 along the trench's edge dived 6 m to the E6.
      const atPortal = (k === 0 || k === n - 1) && near?.run !== undefined && ends(all[near.run]).some(([px, pz]) => Math.hypot(px - x, pz - zz) < 1.5);
      const walled = near?.run !== undefined && run.underpass !== all[near.run].underpass && !atPortal;
      if (near !== undefined && !walled) {
        // A track running on from another, or out of it at a set of points,
        // is at its level, not 5 cm under it: each way of a line stepped down.
        const onTrack = run.style === RAIL && near.style === RAIL;
        pinned[k] = 1; pinValue[k] = near.h - (onTrack ? RAIL_UNDERLAP : UNDERLAP); joins[k] = 1;
        if (onTrack) track[k] = 1;
        continue;
      }
      if (k !== 0 && k !== n - 1) continue;
      for (const e of bridgeEnds) {
        if (Math.hypot(e.x - x, e.z - zz) > e.hw + run.halfWidth) continue;
        const solvedDeck = deck.get(e.bi);
        // On a solved deck's end itself: pinned to its surface above.
        if (e.along === 0 && solvedDeck !== undefined) continue;
        if (e.along > 0 && run.style !== all[e.bi].style) continue;
        const end = solvedDeck !== undefined ? solvedDeck[e.end === 0 ? 0 : solvedDeck.length - 1] : plannedEnd(e.bi, e.end);
        pinned[k] = 1;
        // A line climbs to its bridge; a road's stub climbs or descends from
        // the deck's end toward the road's own ground, at its grade.
        pinValue[k] = run.style === RAIL ? end - e.along * DESIGN[RAIL]!.grade
          : end + Math.max(-e.along * c.grade, Math.min(e.along * c.grade, raw[k] - end));
        if (run.style === RAIL) runsOn[k] = 1;
      }
    }
    // Crossing a solved road on the level, between its own sections: a path
    // over a slip road to a footbridge, say. Pinned only where a section
    // happens to fall on the road, it could pass over it on its ramp — and
    // the bank under it then dams the road. The sections either side take the
    // road's surface where it is crossed, so the way is down at the road there.
    // These win over every other pin: a way that cannot climb from one to the
    // other gives up the other, as it must meet a road it crosses where the
    // road is, however steeply it then climbs away.
    const level = new Uint8Array(n);
    // Nor through an underpass, between its walls: nothing crosses it on the level.
    if (!run.bridge && !run.underpass) {
      for (const other of done) {
        const o = all[other];
        const oz = heights.get(other);
        if (o.bridge || oz === undefined || passesOver[ri].has(other)) continue;
        for (const cr of crossingsOf(run, o)) {
          // Not at an end of either: a way running on from another meets it
          // there, a junction, not a crossing. The E6 tunnel's way at
          // Olskroksmotet was held level to the end of the E6 into it, 4 m
          // over the ground, and its ceilings 7.6 m under that a section on.
          if (ends(run).some(([x, z]) => Math.hypot(x - cr.x, z - cr.z) < 1.5) || ends(o).some(([x, z]) => Math.hypot(x - cr.x, z - cr.z) < 1.5)) continue;
          const h = at(oz, cr.kb) - (run.style === RAIL && o.style === RAIL ? RAIL_UNDERLAP : UNDERLAP);
          for (const k of [Math.floor(cr.ka), Math.ceil(cr.ka)]) {
            if (k < 0 || k >= n || level[k] === 1) continue;
            pinned[k] = 1;
            pinValue[k] = h;
            level[k] = 1;
          }
        }
      }
    }
    // Pins a road cannot climb between: a stub from a street to a raised deck
    // end, or two roads it touches at different levels. Held both, the road
    // jumped metres in one section; the pin further off its own ground goes.
    // A road meeting one it joins climbs harder if it must: a short ramp from
    // a bridge's end up to a roundabout raised over a motorway, say, cut back
    // to the ring's kerb. Up there steeply beats stopping short at a bank.
    // Not between two tracks, though: a train cannot climb from one to the
    // next at 25 %, so one of the pins goes instead. Allowed that, the
    // tracks of a yard humped up and down between their neighbours.
    const climb = Math.max(c.grade, 0.15);
    const joinClimb = Math.max(c.grade, JOIN_CLIMB);
    // Which pin goes: a level crossing's never while there is another to give
    // up, and before even that, a track's end on another's, where the line
    // runs on from one way to the next, or onto its bridge: dropped, the line
    // broke there, a step of metres.
    const keep = (k: number) => runsOn[k] === 1 ? 2 : level[k] === 1 ? 1 : 0;
    for (let changed = true; changed;) {
      changed = false;
      let last = -1;
      for (let k = 0; k < n; k++) {
        if (pinned[k] === 0) continue;
        const tracks = track[k] === 1 && track[last] === 1;
        const grade = tracks ? RAIL_PIN_CLIMB : joins[k] === 1 || joins[last] === 1 ? joinClimb : climb;
        // A short piece of track held at both ends by the tracks it runs on
        // from: it meets both, however steeply, as a step is worse.
        const both = runsOn[k] === 1 && runsOn[last] === 1;
        if (last >= 0 && !both && Math.abs(pinValue[k] - pinValue[last]) > grade * (run.s[k] - run.s[last]) + (tracks ? 0.05 : 0.3)) {
          const further = Math.abs(pinValue[k] - raw[k]) > Math.abs(pinValue[last] - raw[last]) ? k : last;
          // Never a level crossing's pin while there is another to give up.
          const drop = keep(k) > keep(last) ? last : keep(last) > keep(k) ? k : further;
          pinned[drop] = 0;
          changed = true;
          break;
        }
        last = k;
      }
    }
    // A railway has no ceiling: the deck over it is raised instead
    // (`bridges.ts`). Nor under a footbridge: the road keeps its level and
    // the deck rises over it. Held under one, a piece of street the map split
    // off under the bridge sank alone, a step down from the street on either
    // side. See `underCeiling`.
    for (let k = 0; k < n; k++) ceiling[k] = Math.min(ceiling[k], underCeiling(ri, k));
    // Under a road over an underpass, a car's headroom under its surface,
    // across its width and the trough's roof either side.
    for (const c of overUnderpass[ri]) {
      const oz = heights.get(c.over);
      if (oz === undefined) continue;
      const limit = at(oz, c.kOver) - HEADROOM_UNDERPASS, s0 = at(run.s, c.kUnder);
      for (let k = 0; k < n; k++) if (Math.abs(run.s[k] - s0) <= c.span) ceiling[k] = Math.min(ceiling[k], limit);
    }
    // But by the tile's edge the way under keeps the edge's level, a terrain
    // cell in, where the ground cannot be pressed down to it, and then sags
    // down under the decks at its grade: a lower passage there, and no kink.
    // Held under a deck 3 m from the edge, a way through an underpass dropped
    // 2.4 m to it in 7 m, and was lifted back over the edge's ground after.
    for (let p = 0; p < n; p++) {
      if (onEdge[p] === 0 || pinned[p] === 0) continue;
      for (let k = 0; k < n; k++) {
        if (!Number.isFinite(ceiling[k])) continue;
        ceiling[k] = Math.max(ceiling[k], pinValue[p] - c.grade * Math.max(0, Math.abs(run.s[k] - run.s[p]) - EDGE_CELL));
      }
    }

    // And over the ground a terrain cell in from the tile's edge, which the
    // edge's vertices hold up and nothing presses down: there, under it, a
    // road was lifted over it after, section by section (`clearRoadOfGround`).
    // Along the edge at Hålan the motorway, designed 1.4 m under the bank the
    // elevation model has there, humped over it 1.1 m in one section.
    // Only as far as keeps that bank out of the road, though. From the
    // edge's vertex the ground slopes straight down to the vertex a cell in,
    // pressed under the road's outer lane to `EDGE_DITCH` below it
    // (`pressTerrainUnderRoads`), and a kerb `d` in from the edge takes
    // what of that slope is over it, up to `EDGE_WEDGE`; and never more than
    // `EDGE_LIFT` over the road's own ground. Held up to the edge's ground
    // itself, the E6 at Örgrytemotet, 7 m in from the seam under a hillside
    // 4 m over it, was carried 8 m over its other carriageway for 250 m, the
    // motorway's stiff profile cresting far past the sections held up.
    const floor = new Float64Array(n).fill(-Infinity);
    if (!run.underpass && !run.bridge) {
      for (let k = 0; k < n; k++) {
        const a = run.vertices[k];
        for (const v of [a, a + 1]) {
          const x = roads.positions[v * 2], zz = roads.positions[v * 2 + 1];
          if (!inTile(x, zz)) continue;
          for (const [d, ex, ez] of [[x, 0, zz], [width - x, width, zz], [zz, x, 0], [depth - zz, x, depth]]) {
            if (d >= EDGE_CELL) continue;
            const t = d / EDGE_CELL;
            const bank = ground(ex, ez) - (EDGE_WEDGE + (UNDER_ROAD + EDGE_DITCH) * t) / (1 - t);
            floor[k] = Math.max(floor[k], Math.min(bank, raw[k] + EDGE_LIFT));
          }
        }
      }
    }

    // The smoothest profile near the ground that meets every pin: a
    // penalised spline, its stiffness from the class's curve length. Where
    // it passes over a ceiling, that section becomes a pin at the ceiling;
    // where it strays past the class's cut or fill, the ground's pull there
    // is strengthened — never clamped, since a clamp is a corner.
    const target = Float64Array.from(raw);
    const weight = new Float64Array(n).fill(1);
    let arch = false;
    for (const h of overHole[ri]) for (let k = 0; k < n; k++) if (Math.abs(run.s[k] - h.s) <= h.span) weight[k] = 0;
    // A way through an underpass: one arch under everything over it, from
    // the land the road out of each portal is on to the other's, not the
    // ground the elevation model has in it, which it filled in across the
    // gap each bridge left. Followed, the way at Hålan crested at a portal
    // between its four tunnels and dipped under each. Where it leaves the
    // tile it starts level, so the arch the neighbour lays runs on from it.
    if (run.underpass && n >= 2) {
      const beyond = (k: number, from: number) => {
        const dx = run.cx[k] - run.cx[from], dz = run.cz[k] - run.cz[from], len = Math.hypot(dx, dz) || 1;
        return ground(run.cx[k] + dx / len * ARCH_OUT, run.cz[k] + dz / len * ARCH_OUT);
      };
      let i0 = 0, i1 = n - 1;
      while (i0 < n - 1 && outside(i0)) i0++;
      while (i1 > 0 && outside(i1)) i1--;
      const level0 = i0 > 0, level1 = i1 < n - 1;
      const s0 = level0 ? run.s[i0 - 1] : run.s[0], s1 = level1 ? run.s[i1 + 1] : run.s[n - 1];
      const h0 = level0 ? pinValue[i0 - 1] : beyond(0, 1), h1 = level1 ? pinValue[i1 + 1] : beyond(n - 1, n - 2);
      const tOf = (k: number) => (s1 > s0 ? Math.min(1, Math.max(0, (run.s[k] - s0) / (s1 - s0))) : 0.5);
      // Eased flat into a level end; the dip, a half sine, deep enough for every ceiling.
      const base = (t: number) => {
        const e = level0 && level1 ? (1 - Math.cos(Math.PI * t)) / 2 : level0 ? 1 - Math.cos(Math.PI * t / 2) : level1 ? Math.sin(Math.PI * t / 2) : t;
        return h0 + (h1 - h0) * e;
      };
      // Under each ceiling, and up from it either way at the class's grade,
      // no higher than the base. A half sine as deep as every ceiling needed
      // was three times too deep where a deck stands near a portal: the E6
      // into the trench at Olskroksmotet, under slip roads' decks 10 m in,
      // went down 8 m, and the slip road into it fell 22 %.
      for (let k = 0; k < n; k++) {
        let h = base(tOf(k));
        for (let j = 0; j < n; j++) {
          if (Number.isFinite(ceiling[j])) h = Math.min(h, ceiling[j] + c.grade * Math.abs(run.s[k] - run.s[j]));
        }
        target[k] = h;
        weight[k] = 1;
      }
      arch = true;
    }
    const hard = Uint8Array.from(pinned);
    for (let k = 0; k < n; k++) if (hard[k] === 1) target[k] = pinValue[k];
    // A free end lies on its ground. Nothing holds it, and the profile, as
    // stiff as its class, ran on past the land after a dip or a crest: the
    // E6 into the trench at Olskroksmotet, down 5.9 m under Redbergsvägen's
    // decks, came back up 4 m over its ground at the tunnel's portal, and a
    // slip road's start stood 5 m up, carried on from its junction pins.
    // Not under a deck, though: a ceiling near the end holds it down, and
    // the E6 starting under a slip road's deck was pinned to its land 10 m
    // from a ceiling 3.7 m under it.
    const underDeck = (k: number) => ceiling.some((v, j) => v < raw[j] + 0.5 && Math.abs(run.s[j] - run.s[k]) < END_CEILING);
    if (!run.underpass && !arch) for (const k of [0, n - 1]) if (hard[k] === 0 && !underDeck(k)) { hard[k] = 1; target[k] = raw[k]; }
    const stiffness = Math.pow(c.curve, 4);
    let z: Float64Array = raw;
    const dbg = debugAt(run);
    if (dbg) dbgLog(() => crossings.filter(cr => cr.under === ri).map(cr => `  under #${cr.bridge} at s=${cr.sUnder.toFixed(1)} deck=${deck.get(cr.bridge) !== undefined ? at(deck.get(cr.bridge)!, cr.kBridge).toFixed(2) : 'unsolved'} headroom=${cr.headroom} ground=${cr.ground.toFixed(2)} dip=${dip(cr).toFixed(2)} deeper=${(deeper.get(cr) ?? 0).toFixed(2)}`).join('\n'));
    if (dbg) dbgLog(() => `solveRoad #${ri} style=${run.style} hw=${run.halfWidth} n=${n} ends=(${run.cx[0].toFixed(1)},${run.cz[0].toFixed(1)})->(${run.cx[n - 1].toFixed(1)},${run.cz[n - 1].toFixed(1)}) raw=[${Array.from(raw).map(v => v.toFixed(2)).join(' ')}]\n  pins=${Array.from(pinned).map((p, k) => p ? `${k}:${pinValue[k].toFixed(2)}${joins[k] ? 'j' : ''}${onEdge[k] ? 'e' : ''}${level[k] ? 'l' : ''}` : '').filter(Boolean).join(' ')}\n  ceil=${Array.from(ceiling).map((v, k) => Number.isFinite(v) ? `${k}:${v.toFixed(2)}` : '').filter(Boolean).join(' ')} floor=${Array.from(floor).map((v, k) => Number.isFinite(v) ? `${k}:${v.toFixed(2)}` : '').filter(Boolean).join(' ')}`);
    for (let pass = 0; pass < 8; pass++) {
      z = fitProfile(target, run.s, weight, hard, stiffness);
      let changed = false;
      for (let k = 0; k < n; k++) {
        if (hard[k] === 0 && z[k] > ceiling[k] + 1e-3) { hard[k] = 1; target[k] = ceiling[k]; changed = true; }
        if (hard[k] === 0 && z[k] < floor[k] - 1e-3 && floor[k] <= ceiling[k]) { hard[k] = 1; target[k] = floor[k]; changed = true; }
        if (!arch && hard[k] === 0 && Math.abs(z[k] - raw[k]) > c.maxDeviation) { weight[k] *= 8; changed = true; }
      }
      if (!changed) break;
    }

    // The grade: where the land itself is steeper than the class allows, the
    // profile is held to the grade, and then refitted through that — so the
    // climb eases in and out rather than breaking at a corner.
    if (maxGrade(z, run.s) > c.grade + 1e-3) {
      const limited = Float64Array.from(z);
      limitGrade(limited, run.s, c.grade, hard);
      z = fitProfile(limited, run.s, weight.map(w => (w === 0 ? 0 : 4)), hard, stiffness / 16);
    }
    for (let k = 0; k < n; k++) {
      if (hard[k] === 1) z[k] = target[k];
      z[k] = Math.min(z[k], ceiling[k]);
    }
    if (dbg) dbgLog(() => `  -> z=[${Array.from(z).map(v => v.toFixed(2)).join(' ')}]`);
    return z;
  }

  /**
   * Whether a run has an end within 6 m of `globalThis.__wsgDebugAt`, [x, z]
   * in tile metres: the runs `test/replay.live.spec.ts` asks to see solved,
   * logged through `globalThis.__wsgDebugLog`. Both unset in the game.
   */
  function debugAt(run: Run): boolean {
    const at = (globalThis as { __wsgDebugAt?: [number, number] }).__wsgDebugAt;
    if (at === undefined) return false;
    const n = run.cx.length;
    return [0, n - 1].some(k => Math.hypot(run.cx[k] - at[0], run.cz[k] - at[1]) < 6);
  }
  function dbgLog(message: () => string): void {
    const log = (globalThis as { __wsgDebugLog?: (m: string) => void }).__wsgDebugLog;
    if (log !== undefined && (globalThis as { __wsgDebugAt?: unknown }).__wsgDebugAt !== undefined) log(message());
  }

  function solveDeck(bi: number): Float64Array {
    const b = all[bi];
    const n = b.vertices.length;
    const area = areaDeck(bi);
    if (area !== undefined) return new Float64Array(n).fill(area);
    const raw = raws[bi];
    // Ends: onto whatever already arrived there, else the planned climb.
    // An end on another deck's side, a slip road's deck merging onto a
    // flyover halfway along it, is at that deck's surface: planned on its
    // own crossings alone, it ended metres under the flyover's edge.
    // Unless the way runs on from that end as a road: then it passes under
    // the other deck, as the slip roads' decks over the trench at
    // Olskroksmotet pass under the freight railway's viaduct.
    // And a deck beside this one, solved first as the higher: this one is
    // up at its level, one structure, never below its own needs.
    const endValue = (k: number, end: 0 | 1) => {
      const h = solved.heightAt(b.cx[k], b.cz[k], b.halfWidth * 0.9, false, passesOver[bi]);
      if (h !== undefined) return h;
      const onDeck = roadRunsOn(bi, b.cx[k], b.cz[k]) ? undefined : solved.heightAt(b.cx[k], b.cz[k], 0.5, true, passesOver[bi]);
      if (onDeck !== undefined) return onDeck;
      const planned = plannedEnd(bi, end);
      let beside: number | undefined;
      for (const other of done) {
        const oz = heights.get(other);
        if (oz === undefined || !besideDeck(bi, other)) continue;
        const o = all[other];
        const h = o.cx.length < 2 ? undefined : (() => {
          let best: number | undefined, bestD = Infinity;
          for (let j = 0; j < o.cx.length; j++) {
            const d = Math.hypot(o.cx[j] - b.cx[k], o.cz[j] - b.cz[k]);
            if (d < bestD) { bestD = d; best = oz[j]; }
          }
          return best;
        })();
        if (h !== undefined && h > planned && h - planned <= SIDE_DECK_LIFT) beside = Math.max(beside ?? -Infinity, h);
      }
      return beside ?? planned;
    };
    const h0 = endValue(0, 0), h1 = endValue(n - 1, 1);
    if (debugAt(b)) dbgLog(() => `solveDeck #${bi} style=${b.style} ends=(${b.cx[0].toFixed(1)},${b.cz[0].toFixed(1)})->(${b.cx[n - 1].toFixed(1)},${b.cz[n - 1].toFixed(1)}) raw=[${raw[0].toFixed(2)}..${raw[n - 1].toFixed(2)}] h0=${h0.toFixed(2)} h1=${h1.toFixed(2)} planned=${plannedEnd(bi, 0).toFixed(2)}/${plannedEnd(bi, 1).toFixed(2)} reach=${approachReach(bi, 0).toFixed(2)}/${approachReach(bi, n - 1).toFixed(2)}`);
    const s0 = b.s[0], s1 = b.s[n - 1];
    const needs: Array<{ s: number; h: number }> = [];
    for (const cr of crossings) {
      if (cr.bridge !== bi) continue;
      const under = heights.get(cr.under);
      needs.push({ s: cr.sBridge, h: (under !== undefined ? at(under, cr.kUnder) : cr.ground - dip(cr)) + cr.headroom });
    }
    // Straight from end to end. Where that still leaves a crossing short —
    // its approach was solved before the deck and could not climb — the
    // shortfall is made up by one smooth rise over the whole deck, never a
    // peak over the crossing.
    const z: Float64Array = new Float64Array(n);
    const line = (k: number) => h0 + (h1 - h0) * (s1 > s0 ? (b.s[k] - s0) / (s1 - s0) : 0);
    let rise = 0;
    for (const need of needs) {
      const t = s1 > s0 ? (need.s - s0) / (s1 - s0) : 0.5;
      const shape = Math.sin(Math.PI * Math.min(1, Math.max(0, t)));
      const short = need.h - (h0 + (h1 - h0) * t);
      if (short > 0 && shape > 0.05) rise = Math.max(rise, short / shape);
    }
    // A crossing close to an abutment must not amplify a small shortfall
    // into a huge arch in the middle (short / sin(pi*t)). The derivative
    // of this arch is rise*pi/length; reserve only the remaining grade.
    const length = s1 - s0;
    const lineGrade = length > 0 ? Math.abs(h1 - h0) / length : 0;
    rise = Math.min(rise, Math.max(0, cls(b).grade - lineGrade) * length / Math.PI);
    for (let k = 0; k < n; k++) {
      const t = s1 > s0 ? (b.s[k] - s0) / (s1 - s0) : 0;
      // Never under the ground — though at its abutments a deck sits on it.
      z[k] = Math.max(line(k) + rise * Math.sin(Math.PI * t), raw[k] - 0.1);
    }
    z[0] = h0;
    z[n - 1] = h1;
    return z;
  }

  const junctions = levelJunctions(roads, all, heights, design, ringOf, width, depth,
    (a, b) => passesOver[a].has(b) || passesOver[b].has(a));
  // An internal tunnel join is not a portal. Pieces with different widths
  // or styles cannot merge into one ribbon, but must meet at one buried
  // level; pinning each to ground creates a hump and then a mound above it.
  const tunnelKey = (x: number, z: number) => `${x.toFixed(2)},${z.toFixed(2)}`;
  // A surface route can run along a tunnel, not only cross its centreline.
  // Its designed cutting is the cover level too; the original DEM may be
  // several metres higher than the finished rail yard or city street.
  // Road pressing also moves the terrain triangle beside the kerb. Allow
  // one near-field terrain cell for that cutting, rather than placing a
  // roof against the pristine DEM immediately beside a lower road.
  const tunnelTop = (x: number, z: number, reach = 0) => Math.min(overTunnel(x, z), solved.nearest(x, z, reach, false, undefined, true)?.h ?? Infinity);
  const tunnelNodes = new Map<string, { runs: Set<Run>; floor: number }>();
  for (const run of every) if (run.tunnel) {
    for (const k of [0, run.cx.length - 1]) {
      const x = run.cx[k], z = run.cz[k], key = tunnelKey(x, z);
      const floor = tunnelTop(x, z) - tunnelHeadroom(run.style) - TUNNEL_ROOF - TUNNEL_UNDER;
      const node = tunnelNodes.get(key);
      if (node === undefined) tunnelNodes.set(key, { runs: new Set([run]), floor });
      else { node.runs.add(run); node.floor = Math.min(node.floor, floor); }
    }
  }
  const internalFloor = (x: number, z: number) => {
    const node = tunnelNodes.get(tunnelKey(x, z));
    return node !== undefined && node.runs.size > 1 ? node.floor : undefined;
  };
  // A later entrance can lower a surface approach crossing an earlier
  // bore. Revisit the bores with those final cutting levels as cover.
  for (let pass = 0; pass < 2; pass++) for (const run of every) {
    if (run.tunnel && (pass === 0 || designFor(run.style, run.halfWidth) !== undefined)) solveTunnel(run);
  }

  /**
   * Bring the paths running into a portal at (x, z) down to its floor, at
   * `RAMP_GRADE`, as far as each has to go to meet its own level again, and
   * on along the path past its end if it has not by then.
   */
  function rampDown(x: number, z: number, floor: number, portalStyle?: number): void {
    const done = new Set<number>();
    let from: Array<{ x: number; z: number; h: number }> = [{ x, z, h: floor }];
    for (let hop = 0; hop < 16 && from.length > 0; hop++) {
      const next: typeof from = [];
      for (const f of from) {
        all.forEach((o, oi) => {
          if (done.has(oi) || o.tunnel || o.bridge) return;
          if (portalStyle === undefined ? designFor(o.style, o.halfWidth) !== undefined
            : (portalStyle === RAIL) !== (o.style === RAIL) || carriesTraffic(portalStyle) !== carriesTraffic(o.style)) return;
          const zs = heights.get(oi);
          if (zs === undefined) return;
          const m = o.cx.length;
          const end = Math.hypot(o.cx[0] - f.x, o.cz[0] - f.z) < 1.5 ? 0 : Math.hypot(o.cx[m - 1] - f.x, o.cz[m - 1] - f.z) < 1.5 ? m - 1 : -1;
          if (end < 0) return;
          done.add(oi);
          let below = false;
          for (let k = 0; k < m; k++) {
            const along = Math.abs(o.s[k] - o.s[end]);
            const grade = Math.min(RAMP_GRADE, designFor(o.style, o.halfWidth)?.grade ?? RAMP_GRADE);
            const ramp = f.h + grade * along;
            if (ramp < zs[k]) {
              zs[k] = ramp;
              const a = o.vertices[k];
              design[a] = ramp;
              design[a + 1] = ramp;
              if (k === m - 1 - end) below = true;
            }
          }
          if (below) {
            const far = m - 1 - end;
            next.push({ x: o.cx[far], z: o.cz[far], h: zs[far] });
          }
        });
      }
      from = next;
    }
    // These are the same segment objects held by the surface index. Keep
    // them current before another tunnel asks how much cover it has.
    corridors.forEach((seg, i) => {
      const [ri, k] = corridorAt[i];
      if (!done.has(ri)) return;
      const z = heights.get(ri)!;
      seg.h0 = z[k]; seg.h1 = z[k + 1];
    });
  }

  /**
   * A tunnel's floor: straight from portal to portal, at the roads it runs
   * on into there, and dipped under whatever crosses over it, a road on its
   * own surface or a path on the ground, to leave the tunnel's headroom and
   * roof. Where it runs off the tile, it is held at the edge under the
   * ground the neighbour shares, `TUNNEL_COVER` down, as the neighbour holds
   * it. The ground over it is left as it is.
   */
  /** The height of a solved road's end within 1.5 m of (x, z): a road a tunnel runs on into there. */
  function runsOnFrom(x: number, z: number): number | undefined {
    let best: number | undefined, bestD = 1.5;
    all.forEach((o, oi) => {
      const oz = heights.get(oi);
      if (oz === undefined) return;
      for (const k of [0, o.cx.length - 1]) {
        const d = Math.hypot(o.cx[k] - x, o.cz[k] - z);
        if (d < bestD) { bestD = d; best = oz[k]; }
      }
    });
    return best;
  }

  function solveTunnel(run: Run): void {
    const n = run.vertices.length;
    // Beyond the tile's edge, unless only just, along it: a street running
    // along the edge, its centreline a metre over, was pinned flat to the
    // edge's ground by the tile it had left and designed up to the road it
    // joins by the one it was in. Each tile draws the half of it on its side,
    // so the two halves stood half a metre apart. Designed by both, they meet.
    const outside = (k: number) => {
      const x = run.cx[k], z = run.cz[k];
      const over = Math.max(-x, -z, x - width, z - depth);
      if (over <= 0) return false;
      if (over > run.halfWidth + ALONG_EDGE_SLACK) return true;
      const a = Math.max(0, k - 1), b = Math.min(n - 1, k + 1);
      const dx = run.cx[b] - run.cx[a], dz = run.cz[b] - run.cz[a], len = Math.hypot(dx, dz);
      if (len < 1e-6) return true;
      // Across the edge it is beyond: x for the west and east edges, z for the others.
      const across = x < 0 || x > width ? Math.abs(dx) / len : Math.abs(dz) / len;
      return across > ALONG_EDGE_SIN;
    };
    const pinned = new Uint8Array(n), value = new Float64Array(n);
    const rail = run.style === RAIL;
    for (let k = 0; k < n; k++) {
      if (outside(k)) {
        let a = k, b = k;
        while (a > 0 && outside(a - 1)) a--;
        while (b + 1 < n && outside(b + 1)) b++;
        const [j, o] = a > 0 && (b + 1 >= n || k - a <= b - k) ? [a - 1, a] : b + 1 < n ? [b + 1, b] : [-1, k];
        const at = j < 0 ? { x: run.cx[k], z: run.cz[k] } : edgeCrossing(run.cx[j], run.cz[j], run.cx[o], run.cz[o], width, depth);
        // Unless it is a portal in the strip past the edge: a road runs on from it.
        const portal = (k === 0 || k === n - 1) ? runsOnFrom(run.cx[k], run.cz[k]) : undefined;
        pinned[k] = 1;
        value[k] = portal ?? tunnelAtEdge?.(at.x, at.z, run.style) ?? edgeGround(at.x, at.z, run.style, run.halfWidth, false) - TUNNEL_COVER;
        continue;
      }
      if (k !== 0 && k !== n - 1) continue;
      // At the road it runs on into, or the ground a path out of it lies on;
      // not at the road it passes under, beside the portal. Taken for its
      // own, a footway's tunnel began level with the street over it.
      const h = runsOnFrom(run.cx[k], run.cz[k]);
      pinned[k] = 1;
      value[k] = h !== undefined ? h - (rail ? RAIL_UNDERLAP : UNDERLAP)
        : internalFloor(run.cx[k], run.cz[k]) ?? tunnelTop(run.cx[k], run.cz[k]) - tunnelHeadroom(run.style) - TUNNEL_ROOF - TUNNEL_UNDER;
    }
    // Straight between the pins.
    const z = new Float64Array(n);
    let last = -1;
    for (let k = 0; k < n; k++) {
      if (pinned[k] === 0) continue;
      if (last < 0) for (let i = 0; i <= k; i++) z[i] = value[k];
      else for (let i = last; i <= k; i++) z[i] = value[last] + (value[k] - value[last]) * (run.s[i] - run.s[last]) / Math.max(1e-6, run.s[k] - run.s[last]);
      last = k;
    }
    for (let i = Math.max(0, last); i < n; i++) z[i] = value[last];
    // What crosses over it: a ceiling over the floor there.
    const ceiling = new Float64Array(n).fill(Infinity);
    // The ceilings of the roads alone, not the ground's.
    const crossed = new Float64Array(n).fill(Infinity);
    const clear = tunnelHeadroom(run.style) + TUNNEL_ROOF;
    for (const other of every) {
      if (other === run || other.tunnel) continue;
      const oi = all.indexOf(other);
      const oz = oi >= 0 ? heights.get(oi) : undefined;
      for (const cr of crossingsOf(run, other)) {
        // A non-collinear approach intersects its tube at the shared mouth.
        // It is the same road continuing inside, not overhead cover. Counting
        // it as a crossing drove the first floor sections below their portal.
        const entrance = [0, n - 1].some(p =>
          Math.hypot(cr.x - run.cx[p], cr.z - run.cz[p]) < 1.5 &&
          [0, other.cx.length - 1].some(e => Math.hypot(other.cx[e] - run.cx[p], other.cz[e] - run.cz[p]) < 1.5));
        if (entrance) continue;
        const over = (oz !== undefined ? at(oz, cr.kb) : ground(cr.x, cr.z)) - clear;
        for (let k = 0; k < n; k++) {
          if (Math.abs(run.s[k] - at(run.s, cr.ka)) <= other.halfWidth + run.halfWidth + 3) {
            ceiling[k] = Math.min(ceiling[k], over);
            if (carriesTraffic(other.style)) crossed[k] = Math.min(crossed[k], over);
          }
        }
      }
    }
    // And the ground over it, across its whole width, `TUNNEL_UNDER` deeper
    // still: straight from portal to portal, the tube stood out of every dip
    // in the land between them, and what stood on the land stood in it.
    // Not under a road over a path's underpass, though: the road is its
    // cover, and the land beside the road its embankment, not a hill.
    const under = !carriesTraffic(run.style) && crossed.some(Number.isFinite) ? 0 : TUNNEL_UNDER;
    for (let k = 0; k < n; k++) {
      if (outside(k)) continue;
      const a = run.vertices[k];
      // The approach beside a hillside mouth is not overhead cover.
      const reach = Math.min(run.s[k] - run.s[0], run.s[n - 1] - run.s[k]) >= 20 ? 6 : 0;
      const top = Math.min(tunnelTop(run.cx[k], run.cz[k], reach),
        tunnelTop(roads.positions[a * 2], roads.positions[a * 2 + 1], reach), tunnelTop(roads.positions[(a + 1) * 2], roads.positions[(a + 1) * 2 + 1], reach));
      ceiling[k] = Math.min(ceiling[k], top - clear - under);
    }
    let grade = tunnelGrade(run.style, run.halfWidth);
    // Tile-edge heights are shared with the neighbour and cannot be moved
    // by this tile's crossings. If the nominal grade cannot reach a lower
    // crossing, distribute the necessary descent over the available length.
    // Otherwise the later crossing clamp forced a sudden dip in just one
    // section while the rest of the bore stayed at the nominal grade.
    for (let p = 0; p < n; p++) if (pinned[p] && outside(p)) {
      for (let k = 0; k < n; k++) {
        if (outside(k) || Math.min(run.s[k] - run.s[0], run.s[n - 1] - run.s[k]) < 20) continue;
        const distance = Math.abs(run.s[k] - run.s[p]);
        if (distance > 0) grade = Math.max(grade, (value[p] - ceiling[k]) / distance);
      }
    }
    // On flat city land the descent belongs outside the covered tube.
    // Holding its portal at surface level exposed an entire roof and then
    // buryTunnels built a mound over it. Look just inside for natural cover:
    // a hillside portal keeps its level; a cut-and-cover entrance is lowered
    // together with the connected approach roads.
    for (const p of [0, n - 1]) {
      if (outside(p) || internalFloor(run.cx[p], run.cz[p]) !== undefined) continue;
      let covered = Infinity;
      for (let k = 0; k < n; k++) {
        const distance = Math.abs(run.s[k] - run.s[p]);
        // Look through the whole bore, allowing its grade to reach deeper
        // crossings. Looking only 40 m in left a rail tunnel too high to
        // get under a cutting 80 m beyond the portal without a roof hump.
        if (run.s[k] - run.s[0] >= 20 && run.s[n - 1] - run.s[k] >= 20) {
          covered = Math.min(covered, ceiling[k] + under - 0.5 + grade * Math.max(0, distance - 20));
        }
      }
      // A short fragment beside a tile edge may have no sample 20 m inside
      // both mouths. Its real exit still has to meet the shared buried pin
      // at a usable grade; carry the rest of the climb into the approach.
      if (designFor(run.style, run.halfWidth) !== undefined) {
        for (let k = 0; k < n; k++) if (k !== p && pinned[k]) {
          covered = Math.min(covered, value[k] + tunnelGrade(run.style, run.halfWidth) * Math.abs(run.s[k] - run.s[p]));
        }
      }
      if (covered < value[p] - 1) {
        value[p] = covered;
        rampDown(run.cx[p], run.cz[p], covered, run.style);
      }
    }
    // Re-interpolate after moving the portals, retaining shared edge pins.
    for (let a = 0, b = 1; b < n; b++) if (pinned[b]) {
      for (let k = a; k <= b; k++) z[k] = value[a] + (value[b] - value[a]) * (run.s[k] - run.s[a]) / Math.max(1e-6, run.s[b] - run.s[a]);
      a = b;
    }
    // A path's tunnel under a road is an underpass: its portals are at the
    // foot of ramps down to it, as deep as the roads over it need, and the
    // path runs down to each (`rampDown`). Held at the ground, the portal of
    // one under the Rv 40 near Lerum was 9 m from the carriageway over it,
    // too close to get under at the tunnel's grade, and the motorway ran
    // through the tube.
    if (!carriesTraffic(run.style)) {
      for (const p of [0, n - 1]) {
        if (pinned[p] === 0 || outside(p)) continue;
        if (internalFloor(run.cx[p], run.cz[p]) !== undefined) continue;
        // Level through, as one is built: the ramps outside do the climbing.
        let need = Infinity;
        for (let c = 0; c < n; c++) need = Math.min(need, crossed[c]);
        if (need < value[p] - 0.05) {
          value[p] = need;
          rampDown(run.cx[p], run.cz[p], need, designFor(run.style, run.halfWidth) === undefined ? undefined : run.style);
        }
      }
      // Straight between the pins again, now they are lower.
      const ends2 = [0, n - 1];
      for (let k = 0; k < n; k++) {
        const t = run.s[n - 1] > run.s[0] ? (run.s[k] - run.s[0]) / (run.s[n - 1] - run.s[0]) : 0;
        z[k] = Math.min(z[k], value[ends2[0]] + (value[ends2[1]] - value[ends2[0]]) * t);
      }
    }
    // Under each, down at the tunnel's grade and back up, no deeper than the
    // pins let it reach at that grade: a road too close to a portal to pass
    // under is left over the tunnel's roof rather than the floor plunging.
    // A single curve between the portals dived 100 m to pass under a street
    // near one, where the curve was shallow.
    for (let k = 0; k < n; k++) {
      let floor = z[k];
      for (let c = 0; c < n; c++) if (Number.isFinite(ceiling[c])) floor = Math.min(floor, ceiling[c] + grade * Math.abs(run.s[k] - run.s[c]));
      let reach = -Infinity;
      for (let p = 0; p < n; p++) if (pinned[p] === 1) reach = Math.max(reach, value[p] - grade * Math.abs(run.s[k] - run.s[p]));
      // Never over the straight line, though: the pins may be further apart
      // than the grade allows, and the line is then what joins them.
      z[k] = pinned[k] === 1 ? value[k] : Math.min(z[k], Math.max(floor, reach));
    }
    // Rounded where the ramps meet, the pins held, and never up over what
    // the roads crossing over it leave room for: rounded, the bottom of a
    // short dip rose back into the road over it.
    for (let pass = 0; pass < 6; pass++) {
      const prev = Float64Array.from(z);
      for (let k = 1; k + 1 < n; k++) if (pinned[k] === 0) z[k] = Math.min(crossed[k], (prev[k - 1] + 2 * prev[k] + prev[k + 1]) / 4);
    }
    for (let k = 0; k < n; k++) {
      const a = run.vertices[k];
      design[a] = z[k];
      design[a + 1] = z[k];
    }
  }
  corridors.forEach((seg, i) => {
    const [ri, k] = corridorAt[i];
    const z = heights.get(ri);
    if (z === undefined) return;
    seg.h0 = z[k];
    seg.h1 = z[k + 1];
  });
  return { design, corridors, junctions };
}

/** Steepest a road climbs between pins to meet a road it joins, rise per metre. See `solveRoad`. */
const JOIN_CLIMB = 0.25;

/** Over how far a road blends from a junction's plane back into its own profile, metres. */
const JUNCTION_BLEND = 12;
/** Steepest a junction's plane is laid, rise per metre. */
const JUNCTION_GRADE = 0.1;

/**
 * Lay every junction of engineered roads on one plane.
 *
 * Each road arrives at a junction on its own profile, and meets the others
 * there at a point. On a hill, though, one comes in level while another
 * climbs through, and each ribbon is a flat strip square across its own
 * road. So they cut through each other: the level one stands proud of the
 * downhill half of the junction on a step, sinks under the uphill half, and
 * grass shows in the wedges between their square ends.
 *
 * Here, wherever three or more road ends meet, or a road ends against
 * another's side, the junction is given a plane fitted through where each
 * road leaves it. Every ribbon within it is laid on that plane, tilted across
 * as the plane is, so where they overlap they coincide; the class lifts then
 * keep the bigger road on top. Each road blends back into its own profile
 * over `JUNCTION_BLEND` metres. The plate returned for each junction fills the
 * gaps between the square-cut ends. Roundabouts, already level, the ends of
 * bridges and the tile's edge are left as they are.
 */
function levelJunctions(
  roads: RoadRibbons, all: readonly Run[], heights: Map<number, Float64Array>, design: Float32Array,
  ringOf: Map<number, number>, width: number, depth: number,
  /**
   * Whether two runs only pass over or under each other: never one junction.
   * The road out of an underpass's portal, under the edge of the road over
   * it, was taken for a T into it, and the road over levelled down to it.
   */
  apart: (a: number, b: number) => boolean = () => false,
): JunctionPlate[] {
  // Not railways. A track is never tilted onto a road junction's plane: at a
  // level crossing the road already meets the rails where they are, and a
  // set of points laid on a plane at up to 10 % put a hump in the line.
  const roadRuns = all.map((r, i) => i).filter(i => !all[i].bridge && all[i].style !== RAIL &&
    designFor(all[i].style, all[i].halfWidth) !== undefined && heights.has(i));
  const bridgeEnds = all.filter(r => r.bridge).flatMap(r => [[r.cx[0], r.cz[0]], [r.cx[r.cx.length - 1], r.cz[r.cz.length - 1]]]);
  type Member = { ri: number; k: number; through: boolean };
  type Node = { x: number; z: number; members: Member[] };
  const nodes: Node[] = [];
  const join = (x: number, z: number, m: Member) => {
    const near = nodes.find(n => Math.hypot(n.x - x, n.z - z) < 2.5);
    if (near === undefined) { nodes.push({ x, z, members: [m] }); return; }
    if (near.members.some(o => o.ri === m.ri && o.k === m.k)) return;
    near.members.push(m);
  };
  // Ends meeting ends.
  for (const ri of roadRuns) {
    const r = all[ri];
    const n = r.cx.length;
    join(r.cx[0], r.cz[0], { ri, k: 0, through: false });
    join(r.cx[n - 1], r.cz[n - 1], { ri, k: n - 1, through: false });
  }
  // Side roads end at the kerb of the road they join, cut back from the node
  // it shares with that road's ways (see `sideRoadTrims` in `roads.ts`): a
  // lone end within reach of a node is one of its roads.
  for (let i = nodes.length - 1; i >= 0; i--) {
    const lone = nodes[i];
    if (lone.members.length !== 1) continue;
    let home: Node | undefined, homeD = Infinity;
    for (const node of nodes) {
      if (node === lone || node.members.length < 2) continue;
      if (node.members.some(m => apart(m.ri, lone.members[0].ri))) continue;
      const reach = 2 * Math.max(...node.members.map(m => all[m.ri].halfWidth)) + 1;
      const d = Math.hypot(node.x - lone.x, node.z - lone.z);
      if (d <= reach && d < homeD) { homeD = d; home = node; }
    }
    if (home === undefined) continue;
    home.members.push(lone.members[0]);
    nodes.splice(i, 1);
  }
  // Ends against another road's side: a T. The road it meets runs through.
  for (const node of nodes) {
    for (const ri of roadRuns) {
      if (node.members.some(m => m.ri === ri || apart(m.ri, ri))) continue;
      const r = all[ri];
      let best = -1, bestD = r.halfWidth + 1.5;
      for (let k = 1; k + 1 < r.cx.length; k++) {
        const d = Math.hypot(r.cx[k] - node.x, r.cz[k] - node.z);
        if (d < bestD) { bestD = d; best = k; }
      }
      if (best < 0) continue;
      node.members.push({ ri, k: best, through: true });
      // The junction's middle is on the road running through, where the side
      // road's line meets it, not at the side road's end at the kerb.
      if (node.members.length === 2) {
        const x0 = r.cx[best - 1], z0 = r.cz[best - 1], ex = r.cx[best + 1] - x0, ez = r.cz[best + 1] - z0;
        const t = Math.max(0, Math.min(1, ((node.x - x0) * ex + (node.z - z0) * ez) / (ex * ex + ez * ez || 1)));
        node.x = x0 + ex * t;
        node.z = z0 + ez * t;
      }
    }
  }

  // Side roads on both sides of a road the map did not split are two Ts at
  // one place: one junction.
  for (let i = nodes.length - 1; i >= 0; i--) {
    const other = nodes.find((n, j) => j < i && Math.hypot(n.x - nodes[i].x, n.z - nodes[i].z) < 3);
    if (other === undefined) continue;
    for (const m of nodes[i].members) {
      if (!other.members.some(o => o.ri === m.ri && Math.abs(o.k - m.k) <= 1)) other.members.push(m);
    }
    nodes.splice(i, 1);
  }

  const plates: JunctionPlate[] = [];
  for (const node of nodes) {
    const exits = node.members.reduce((sum, m) => sum + (m.through ? 2 : 1), 0);
    const distinct = new Set(node.members.map(m => m.ri)).size;
    if (exits < 3 || distinct < 2) continue;
    if (node.members.some(m => ringOf.has(m.ri))) continue;
    if (node.x < 5 || node.z < 5 || node.x > width - 5 || node.z > depth - 5) continue;
    const radius = Math.max(...node.members.map(m => all[m.ri].halfWidth)) + 2.5;
    if (bridgeEnds.some(([bx, bz]) => Math.hypot(bx - node.x, bz - node.z) < radius + JUNCTION_BLEND)) continue;

    // Where each road leaves the junction, and its height there: a few metres
    // out along it, each way for a road running through.
    const samples: Array<[number, number, number, number]> = [];
    let centre = 0;
    for (const m of node.members) {
      const r = all[m.ri], z = heights.get(m.ri)!;
      centre += z[m.k];
      for (const dir of m.through ? [-1, 1] : [m.k === 0 ? 1 : -1]) {
        let k = m.k;
        while (k + dir >= 0 && k + dir < r.cx.length && Math.hypot(r.cx[k] - node.x, r.cz[k] - node.z) < radius + 3) k += dir;
        samples.push([r.cx[k], r.cz[k], z[k], 1]);
      }
    }
    centre /= node.members.length;
    samples.push([node.x, node.z, centre, 2]);
    const plane = fitPlane(samples, node.x, node.z);
    const slope = Math.hypot(plane.gx, plane.gz);
    if (slope > JUNCTION_GRADE) { plane.gx *= JUNCTION_GRADE / slope; plane.gz *= JUNCTION_GRADE / slope; }
    const at = (x: number, z: number) => plane.h + plane.gx * (x - node.x) + plane.gz * (z - node.z);

    // Onto the plane, and back into each road's own profile further out.
    const outline: number[] = [];
    let style = Infinity;
    for (const ri of new Set(node.members.map(m => m.ri))) {
      const r = all[ri], z = heights.get(ri)!;
      style = Math.min(style, r.style);
      for (let k = 0; k < r.cx.length; k++) {
        const d = Math.hypot(r.cx[k] - node.x, r.cz[k] - node.z);
        if (d >= radius + JUNCTION_BLEND) continue;
        const t = d <= radius ? 1 : 1 - (d - radius) / JUNCTION_BLEND;
        const w = t * t * (3 - 2 * t);
        const a = r.vertices[k];
        for (const v of [a, a + 1]) {
          const x = roads.positions[v * 2], vz = roads.positions[v * 2 + 1];
          design[v] = design[v] * (1 - w) + at(x, vz) * w;
          if (d <= radius + 1) outline.push(x, vz);
        }
        z[k] = z[k] * (1 - w) + at(r.cx[k], r.cz[k]) * w;
      }
    }
    const hull = convexHull(outline);
    if (hull.length >= 6) plates.push({ outline: hull, cx: node.x, cz: node.z, h: plane.h, gx: plane.gx, gz: plane.gz, style });
  }
  return plates;
}

/** Least-squares plane through weighted (x, z, height, weight) samples, about (cx, cz). */
function fitPlane(samples: ReadonlyArray<readonly [number, number, number, number]>, cx: number, cz: number): { h: number; gx: number; gz: number } {
  // Normal equations for h + gx·dx + gz·dz, a touch of ridge on the slope so
  // samples in a line still give a plane.
  let sw = 0, sx = 0, sz = 0, sxx = 0, szz = 0, sxz = 0, sy = 0, sxy = 0, szy = 0;
  for (const [x, z, y, w] of samples) {
    const dx = x - cx, dz = z - cz;
    sw += w; sx += w * dx; sz += w * dz; sxx += w * dx * dx; szz += w * dz * dz; sxz += w * dx * dz;
    sy += w * y; sxy += w * dx * y; szy += w * dz * y;
  }
  const ridge = 1e-3 * (sxx + szz) + 1e-6;
  const m = [[sw, sx, sz], [sx, sxx + ridge, sxz], [sz, sxz, szz + ridge]];
  const b = [sy, sxy, szy];
  const det = (q: number[][]) =>
    q[0][0] * (q[1][1] * q[2][2] - q[1][2] * q[2][1]) - q[0][1] * (q[1][0] * q[2][2] - q[1][2] * q[2][0]) + q[0][2] * (q[1][0] * q[2][1] - q[1][1] * q[2][0]);
  const d = det(m);
  if (Math.abs(d) < 1e-9) return { h: sy / sw, gx: 0, gz: 0 };
  const col = (c: number) => m.map((row, i) => row.map((v, j) => (j === c ? b[i] : v)));
  return { h: det(col(0)) / d, gx: det(col(1)) / d, gz: det(col(2)) / d };
}

/** Convex hull of x, z pairs (monotone chain), as x, z pairs. */
function convexHull(flat: number[]): number[] {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < flat.length; i += 2) pts.push([flat[i], flat[i + 1]]);
  pts.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length < 3) return [];
  const cross = (o: [number, number], a: [number, number], b: [number, number]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Array<[number, number]> = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Array<[number, number]> = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)].flat();
}

/** How far (x, z) is from a run's centreline, metres. */
function distanceToRun(run: Run, x: number, z: number): number {
  let best = Infinity;
  for (let k = 0; k + 1 < run.cx.length; k++) {
    const ux = run.cx[k + 1] - run.cx[k], uz = run.cz[k + 1] - run.cz[k], len2 = ux * ux + uz * uz;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - run.cx[k]) * ux + (z - run.cz[k]) * uz) / len2)) : 0;
    best = Math.min(best, Math.hypot(run.cx[k] + ux * t - x, run.cz[k] + uz * t - z));
  }
  return best;
}

/** Whether (x, z) lies on `run`'s surface along its side: within its half-width of its centreline, and not at either end. */
function onSideOf(run: Run, x: number, z: number): boolean {
  const n = run.cx.length;
  for (let k = 0; k + 1 < n; k++) {
    const ux = run.cx[k + 1] - run.cx[k], uz = run.cz[k + 1] - run.cz[k], len2 = ux * ux + uz * uz;
    if (len2 < 1e-9) continue;
    const t = Math.max(0, Math.min(1, ((x - run.cx[k]) * ux + (z - run.cz[k]) * uz) / len2));
    if (Math.hypot(run.cx[k] + ux * t - x, run.cz[k] + uz * t - z) > run.halfWidth) continue;
    const along = run.s[k] + (run.s[k + 1] - run.s[k]) * t;
    return along - run.s[0] > 3 && run.s[n - 1] - along > 3;
  }
  return false;
}

/**
 * Solve order. A slip road comes after both the motorway below and the road
 * above it, since it is the one that has to meet them both.
 */
function rankOf(run: Run): number {
  if (run.style === 0 && run.halfWidth < LINK_HALF_WIDTH) return 3.5;
  return RANK[run.style] ?? 6;
}

/**
 * Roundabouts: chains of roads, each starting where the last ended, that
 * close into a small, smoothly curving loop. A roundabout is mapped as a
 * ring of one-way ways (split wherever something joins it, and at its
 * bridges), all running the same way round, so the chain is followed end to
 * start. A city block can close a loop too, but it has corners; a
 * roundabout turns a little at every step.
 */
function findRings(all: readonly Run[]): number[][] {
  const candidates = all.map((r, i) => i).filter(i => all[i].style >= 1 && all[i].style <= 4);
  const JOIN = 1.5;
  const startsAt = (i: number, x: number, z: number) => Math.hypot(all[i].cx[0] - x, all[i].cz[0] - z) <= JOIN;
  const rings: number[][] = [];
  const used = new Set<number>();
  for (const first of candidates) {
    if (used.has(first)) continue;
    const chain = [first];
    let length = all[first].s[all[first].s.length - 1] - all[first].s[0];
    let closed = false;
    while (chain.length <= 10 && length <= 500) {
      const last = all[chain[chain.length - 1]];
      const ex = last.cx[last.cx.length - 1], ez = last.cz[last.cz.length - 1];
      if (chain.length > 1 && startsAt(first, ex, ez)) { closed = true; break; }
      const next = candidates.find(j => !chain.includes(j) && !used.has(j) && startsAt(j, ex, ez));
      if (next === undefined) {
        if (chain.length === 1 && startsAt(first, ex, ez)) closed = true; // one way round
        break;
      }
      chain.push(next);
      length += all[next].s[all[next].s.length - 1] - all[next].s[0];
    }
    if (!closed || length > 500) continue;
    // Compact, and turning steadily: the heading turns through a full circle,
    // never more than 40 degrees at a single step.
    const xs: number[] = [], zs: number[] = [];
    for (const ri of chain) for (let k = 0; k < all[ri].cx.length; k++) { xs.push(all[ri].cx[k]); zs.push(all[ri].cz[k]); }
    const mx = xs.reduce((a, b) => a + b, 0) / xs.length, mz = zs.reduce((a, b) => a + b, 0) / zs.length;
    const radius = Math.max(...xs.map((x, i) => Math.hypot(x - mx, zs[i] - mz)));
    if (radius > 90 || radius < 6) continue;
    let turned = 0, sharpest = 0;
    let prev: number | undefined;
    for (let i = 0; i + 1 < xs.length; i++) {
      const dx = xs[i + 1] - xs[i], dz = zs[i + 1] - zs[i];
      if (Math.hypot(dx, dz) < 0.5) continue;
      const heading = Math.atan2(dz, dx);
      if (prev !== undefined) {
        const turn = Math.atan2(Math.sin(heading - prev), Math.cos(heading - prev));
        turned += turn;
        sharpest = Math.max(sharpest, Math.abs(turn));
      }
      prev = heading;
    }
    if (Math.abs(turned) < Math.PI * 1.6 || sharpest > (40 * Math.PI) / 180) continue;
    for (const ri of chain) used.add(ri);
    rings.push(chain);
  }
  return rings;
}

// --- reshaping the ground ---------------------------------------------------------

/**
 * Move the terrain grid to meet the designed roads, in place: heights, the
 * drawn positions, the skirt and the normals. `pristine` is the grid as it
 * was built, which every reshaping starts from, so doing it again — when a
 * tile's roads are rebuilt — gives the same ground rather than digging deeper.
 *
 * Each vertex follows the road whose kerb it is nearest, fully within most of
 * a cell of the kerb and fading out over the class's reach, so the slope
 * between it and untouched ground lands in the next cell out. The tile's own
 * edge is never moved: it is shared with the neighbouring tile.
 */
export function carveTerrain(
  mesh: TerrainMeshData,
  pristine: Float32Array,
  corridors: readonly CorridorSegment[],
  pads: readonly BuildingPad[] = [],
  /** How far each style's drawn surface stands over its design line, metres. */
  lift: (style: number) => number = () => 0,
  /** Per grid vertex, how built-up the land is, 0..1: see `URBAN_FILL`. */
  urban?: Float32Array,
): void {
  const { gridRes, gridHeights, positions } = mesh;
  const side = gridRes + 1;
  gridHeights.set(pristine);
  const padCells = hashPads(pads);

  if (corridors.length > 0 || pads.length > 0) {
    // Corridor segments by grid cell, generous enough for the widest reach.
    const CELL = 16;
    const cells = new Map<number, CorridorSegment[]>();
    for (const seg of corridors) {
      const pad = seg.halfWidth + MAX_SLOPE;
      const x0 = Math.floor((Math.min(seg.x0, seg.x1) - pad) / CELL), x1 = Math.floor((Math.max(seg.x0, seg.x1) + pad) / CELL);
      const z0 = Math.floor((Math.min(seg.z0, seg.z1) - pad) / CELL), z1 = Math.floor((Math.max(seg.z0, seg.z1) + pad) / CELL);
      for (let cx = x0; cx <= x1; cx++) {
        for (let cz = z0; cz <= z1; cz++) {
          const key = cx * 100003 + cz;
          let list = cells.get(key);
          if (list === undefined) cells.set(key, (list = []));
          list.push(seg);
        }
      }
    }

    for (let i = 1; i < gridRes; i++) {
      for (let j = 1; j < gridRes; j++) {
        const vi = i * side + j;
        const x = positions[vi * 3], z = positions[vi * 3 + 2];
        const list = cells.get(Math.floor(x / CELL) * 100003 + Math.floor(z / CELL)) ?? [];
        const nearPads = padCells.get(Math.floor(x / PAD_CELL) * 100003 + Math.floor(z / PAD_CELL));
        if (list.length === 0 && nearPads === undefined) continue;
        let bestBeyond = Infinity;
        let target = 0, weight = 0;
        // Under a road's surface, or at its kerb, the ground goes below the
        // lowest road there: where a ramp runs beside its motorway, the
        // ground under both must clear both.
        let covered = Infinity, deckCap = Infinity;
        let stair = false;
        for (const seg of list) {
          const { d, t } = project(seg, x, z);
          const beyond = d - seg.halfWidth;
          const h = seg.h0 + (seg.h1 - seg.h0) * t + (seg.style === undefined ? 0 : lift(seg.style)) - UNDER_ROAD;
          // The ground under a deck is what the bridge crosses: a deck only
          // cuts ground that would come up through it, and never raises the
          // land to itself. Else a footbridge over a street stood on a
          // five-metre mound either side of it.
          if (seg.bridge === true && h >= pristine[vi]) continue;
          // Nor does it hold the ground under it up to itself: it caps it.
          // Taken for the road the ground is under, beside the kerb of the
          // motorway in the cutting it crosses, the ground stood up to the
          // deck, and the motorway was lifted onto it.
          if (seg.bridge === true) {
            if (beyond <= 0.5) deckCap = Math.min(deckCap, h);
            continue;
          }
          if (beyond <= 0.5 && h < covered) { covered = h; stair = seg.stair === true; }
          // The deeper the cut or the higher the fill, the wider its slope:
          // about 1:2, at least the class's reach, at most five cells.
          const full = FULL_BAND;
          const reach = Math.min(MAX_SLOPE, Math.max(seg.reach * REACH_UNIT, full + 2 * Math.abs(h - pristine[vi])));
          if (beyond > reach || beyond >= bestBeyond) continue;
          bestBeyond = beyond;
          target = h;
          if (covered === Infinity) stair = seg.stair === true;
          // Fully on the road's level out to 60% of a cell past the kerb,
          // then eased back to the land by the reach.
          const u = beyond <= full ? 0 : (beyond - full) / Math.max(1e-6, reach - full);
          weight = 1 - u * u * (3 - 2 * u);
        }
        if (covered < Infinity) { target = covered; weight = 1; }
        // A building's pad. Under the building itself its own pad holds —
        // over a neighbour's pad and over a road's side slope or verge, else
        // the ground under a house next to a road or another house stays low
        // and the house hangs in the air. Around it, the stronger of pad and
        // road. Under a road's own surface, always the road.
        let fromPad = false;
        if (nearPads !== undefined && covered === Infinity) {
          let padTarget = 0, padW = 0, inside = false;
          for (const pad of nearPads) {
            if (isInside(pad, x, z)) { padTarget = pad.level; padW = 1; inside = true; break; }
            const w = padWeight(pad, x, z, pristine[vi]);
            if (w > padW) { padW = w; padTarget = pad.level; }
          }
          if (inside || padW > weight) { weight = padW; target = padTarget; fromPad = true; }
        }
        let rise = (target - pristine[vi]) * weight;
        if (!fromPad && !stair && urban !== undefined && rise > URBAN_FILL) rise += (URBAN_FILL - rise) * urban[vi];
        if (weight > 0) gridHeights[vi] = pristine[vi] + rise;
        if (gridHeights[vi] > deckCap) gridHeights[vi] = deckCap;
      }
    }
  }

  refreshTerrainGeometry(mesh);
}

/**
 * A building's pad: its oriented footprint, and the level the ground is cut
 * and filled to under it, so a house on a slope stands on a terrace instead
 * of being half buried uphill and floating downhill.
 */
export interface BuildingPad {
  cx: number;
  cz: number;
  /** Unit axes of the footprint, and its half extents along them. */
  ux: number;
  uz: number;
  hw: number;
  hd: number;
  level: number;
}

/**
 * Most a road raises built-up ground, metres. An earth bank is how a road
 * on fill crosses fields; in a city a raised approach is a ramp between
 * retaining walls and the pavement round it stays level. Past this the road
 * stands proud and `roadEmbankments` draws its walls. Cuttings are never
 * capped: the ground must not come up through a road.
 */
const URBAN_FILL = 0.5;

/** Ground level with the pad out this far past the walls, metres. */
const PAD_MARGIN = 1.2;
const PAD_CELL = 16;

function hashPads(pads: readonly BuildingPad[]): Map<number, BuildingPad[]> {
  const cells = new Map<number, BuildingPad[]>();
  for (const pad of pads) {
    const r = Math.hypot(pad.hw, pad.hd) + PAD_MARGIN + 20;
    for (let cx = Math.floor((pad.cx - r) / PAD_CELL); cx <= Math.floor((pad.cx + r) / PAD_CELL); cx++) {
      for (let cz = Math.floor((pad.cz - r) / PAD_CELL); cz <= Math.floor((pad.cz + r) / PAD_CELL); cz++) {
        const key = cx * 100003 + cz;
        let list = cells.get(key);
        if (list === undefined) cells.set(key, (list = []));
        list.push(pad);
      }
    }
  }
  return cells;
}

/** Whether (x, z) is under the building itself, its footprint grown by the pad margin. */
function isInside(pad: BuildingPad, x: number, z: number): boolean {
  const dx = x - pad.cx, dz = z - pad.cz;
  return Math.abs(dx * pad.ux + dz * pad.uz) <= pad.hw + PAD_MARGIN * 0.5 &&
    Math.abs(-dx * pad.uz + dz * pad.ux) <= pad.hd + PAD_MARGIN * 0.5;
}

/**
 * How strongly a pad holds the ground at (x, z): fully on the footprint and
 * its margin, then easing back to the land over a slope about twice as wide
 * as the cut or fill is deep.
 */
function padWeight(pad: BuildingPad, x: number, z: number, land: number): number {
  const dx = x - pad.cx, dz = z - pad.cz;
  const u = Math.abs(dx * pad.ux + dz * pad.uz) - pad.hw;
  const v = Math.abs(-dx * pad.uz + dz * pad.ux) - pad.hd;
  const beyond = Math.hypot(Math.max(0, u), Math.max(0, v)) - PAD_MARGIN;
  if (beyond <= 0) return 1;
  const slope = Math.min(20, Math.max(1.5, 2 * Math.abs(land - pad.level)));
  if (beyond >= slope) return 0;
  const t = beyond / slope;
  return 1 - t * t * (3 - 2 * t);
}

/** Least rise across a footprint worth a pad, metres: on flatter ground a house sits as it is. */
const PAD_MIN_RISE = 0.4;
/**
 * A pad is a house's terrace, not a city block's. OSM outlines whole blocks,
 * halls and courtyards as one footprint up to a few hundred metres long, and
 * levelling one of those on a hillside cut a ten-metre terrace with cliffs
 * into the streets round it. Real buildings that size step their foundations
 * down the slope and the ground stays as it is; here they stand on their
 * lowest ground with the uphill side dug in, as their buried skirt allows.
 * Longest side, metres, and most rise across a footprint that still gets a pad.
 */
const PAD_MAX_SIDE = 40;
const PAD_MAX_RISE = 2.5;
/** Where between its lowest and highest corner a pad is levelled: mostly cut, a little fill. */
const PAD_LEVEL = 0.35;

/**
 * The pads for a tile's buildings, from their four ground probes (corners in
 * order round the footprint) and the land as it was: one per building range,
 * or undefined where the ground is flat enough to build on as it is.
 */
export function buildingPads(probes: Float32Array, count: number, land: (x: number, z: number) => number): Array<BuildingPad | undefined> {
  const out: Array<BuildingPad | undefined> = [];
  for (let b = 0; b < count; b++) {
    const c = probes.subarray(b * 8, b * 8 + 8);
    const hs = [0, 1, 2, 3].map(k => land(c[k * 2], c[k * 2 + 1]));
    const lo = Math.min(...hs), hi = Math.max(...hs);
    const e1x = c[2] - c[0], e1z = c[3] - c[1], e2x = c[6] - c[0], e2z = c[7] - c[1];
    const l1 = Math.hypot(e1x, e1z), l2 = Math.hypot(e2x, e2z);
    if (!Number.isFinite(lo) || hi - lo < PAD_MIN_RISE || hi - lo > PAD_MAX_RISE ||
      l1 < 0.5 || l2 < 0.5 || Math.max(l1, l2) > PAD_MAX_SIDE) { out.push(undefined); continue; }
    out.push({
      cx: (c[0] + c[2] + c[4] + c[6]) / 4,
      cz: (c[1] + c[3] + c[5] + c[7]) / 4,
      ux: e1x / l1, uz: e1z / l1,
      hw: l1 / 2, hd: l2 / 2,
      level: lo + (hi - lo) * PAD_LEVEL,
    });
  }
  return out;
}

/**
 * Rewrite the drawn surface from `gridHeights`: vertex Y, the skirt hanging
 * off the edge, the normals and the height range. Mirrors `buildTerrainMesh`.
 */
export function refreshTerrainGeometry(mesh: TerrainMeshData): void {
  const { gridRes, gridHeights, positions, normals, cellWidth } = mesh;
  const side = gridRes + 1;
  const gridVerts = side * side;

  // The skirt: the boundary loop in the order `buildTerrainMesh` walks it,
  // each vertex hanging a fixed depth under its edge vertex. The depth is read
  // off the mesh before the edge moves, so it survives any reshaping.
  const ring: number[] = [];
  for (let j = 0; j < gridRes; j++) ring.push(j);
  for (let i = 0; i < gridRes; i++) ring.push(i * side + (side - 1));
  for (let j = gridRes; j > 0; j--) ring.push((side - 1) * side + j);
  for (let i = gridRes; i > 0; i--) ring.push(i * side);
  const hasSkirt = positions.length >= (gridVerts + ring.length) * 3;
  const skirtDepth = ring.map((src, k) =>
    hasSkirt ? positions[src * 3 + 1] - positions[(gridVerts + k) * 3 + 1] : 0);

  let min = Infinity, max = -Infinity;
  for (let vi = 0; vi < gridVerts; vi++) {
    const h = gridHeights[vi];
    positions[vi * 3 + 1] = h;
    if (h < min) min = h;
    if (h > max) max = h;
  }
  mesh.minHeight = min;
  mesh.maxHeight = max;

  // Normals by central differences in metres, as the mesh was built.
  for (let i = 0; i < side; i++) {
    const zPrev = positions[(i > 0 ? i - 1 : 0) * side * 3 + 2];
    const zNext = positions[(i < side - 1 ? i + 1 : side - 1) * side * 3 + 2];
    const dz = zNext - zPrev;
    for (let j = 0; j < side; j++) {
      const jPrev = j > 0 ? j - 1 : 0;
      const jNext = j < side - 1 ? j + 1 : side - 1;
      const dx = (jNext - jPrev) * cellWidth;
      const dhdx = dx !== 0 ? (gridHeights[i * side + jNext] - gridHeights[i * side + jPrev]) / dx : 0;
      const dhdz = dz !== 0
        ? (gridHeights[(i < side - 1 ? i + 1 : side - 1) * side + j] - gridHeights[(i > 0 ? i - 1 : 0) * side + j]) / dz
        : 0;
      const len = Math.hypot(dhdx, 1, dhdz);
      const vi = i * side + j;
      normals[vi * 3] = -dhdx / len;
      normals[vi * 3 + 1] = 1 / len;
      normals[vi * 3 + 2] = -dhdz / len;
    }
  }

  if (!hasSkirt) return;
  ring.forEach((src, k) => {
    const dst = gridVerts + k;
    positions[dst * 3 + 1] = gridHeights[src] - skirtDepth[k];
    normals.copyWithin(dst * 3, src * 3, src * 3 + 3);
  });
}

/** How far past the tile's edge, beyond its half-width, a road running along the edge is still designed as in the tile, metres. */
const ALONG_EDGE_SLACK = 1;
/** A way's end this close inside the tile's edge is on it, metres. */
const END_ON_EDGE = 0.5;
/** A road is taken to run along the edge when it crosses it at a sine under this: within 30°. */
const ALONG_EDGE_SIN = 0.5;

/**
 * Most the ground is pressed down under a road, metres: down a ramp to an
 * underpass, the bank of a street beside a motorway in a cutting 11 m deep.
 * Never under the road itself, so this is only a backstop.
 */
const PRESS_MOST = 15;
/** How far out past an underpass's portal the land its arch starts from is taken, metres. */
const ARCH_OUT = 15;
/** Deepest an underpass is held under the ground at the tile's edge, metres. */
const ARCH_DEEPEST = 3;
/** How near a ceiling a road's free end may be and still be pinned to its ground, metres. */
const END_CEILING = 25;
/** Least rate a ceiling rises at between two decks' spans, so a road under decks at different heights is never asked to climb faster. */
const CEILING_CLIMB = 0.15;
/** Decks side by side, one structure: within this of kerb to kerb, metres, running within this cosine of parallel, and the lower lifted to the higher by at most this, metres. */
const SIDE_DECK_GAP = 1.5;
const SIDE_DECK_COS = 0.9;
const SIDE_DECK_LIFT = 4;
/** How far in from the tile's edge the ground is held up by the edge's vertices, which never move: about a terrain cell, metres. */
const EDGE_CELL = 6;
/** How far the bank the edge's vertices hold up may stand into a road over its kerb, metres. */
const EDGE_WEDGE = 0.5;
/** Most the edge's ground holds a road up over its own ground, metres: under a higher bank the road keeps its line. */
const EDGE_LIFT = 1;
/**
 * Deepest the ground a cell in from the edge is pressed under a road's outer
 * lane, so the bank's slope up from there to the edge's vertex passes under
 * the kerb, metres. Under the road, nothing of it shows.
 */
const EDGE_DITCH = 3;

/**
 * Push the ground down wherever it still comes up into a designed road.
 *
 * A terrain triangle is up to 14 m across, larger than a carriageway, so the
 * corridor cannot always reshape all of it; the road used to be lifted over
 * what was left, and every lift was a bump. The road is the design and the
 * ground is what gives way: at each sample across the road where the surface
 * intrudes, the three vertices of the triangle it lies in are lowered by the
 * intrusion. Vertices on the tile edge stay, shared as they are with the
 * neighbour; what they leave is for `clearRoadOfGround`.
 */
export function pressTerrainUnderRoads(
  mesh: TerrainMeshData,
  roads: RoadRibbons,
  heights: Float32Array,
  lift: (style: number) => number,
): boolean {
  const { gridRes, gridHeights, width, depth } = mesh;
  const side = gridRes + 1;
  const p = roads.positions;
  const quads = Math.floor(roads.indices.length / 6);
  const ACROSS = [0, 0.25, 0.5, 0.75, 1];
  let changed = false;
  // Never under the tile's own ground past it: a road there, in the strip of
  // the neighbour's the tile holds, was clamped onto the edge's cells, where
  // the edge vertices that cannot move held the ground up, and each sample
  // pressed the one beside them down again by all of it: pits 20 to 140 m
  // deep a row in from the edge. And never more than `PRESS_MOST` in all.
  const before = Float32Array.from(gridHeights);
  // Whether grid vertex vi lies under the lane of the quad starting at
  // ribbon vertex a, a little in from its kerbs, the quad run on 5 m past
  // each section for the one next to it.
  const underLane = (vi: number, a: number) => {
    const x0 = (p[a * 2] + p[(a + 1) * 2]) / 2, z0 = (p[a * 2 + 1] + p[(a + 1) * 2 + 1]) / 2;
    const x1 = (p[(a + 2) * 2] + p[(a + 3) * 2]) / 2, z1 = (p[(a + 2) * 2 + 1] + p[(a + 3) * 2 + 1]) / 2;
    const hw = Math.hypot(p[(a + 1) * 2] - p[a * 2], p[(a + 1) * 2 + 1] - p[a * 2 + 1]) / 2;
    const dx = x1 - x0, dz = z1 - z0, len = Math.hypot(dx, dz);
    if (len < 1e-6) return false;
    const vx = (vi % side) * width / gridRes - x0, vz = Math.floor(vi / side) * depth / gridRes - z0;
    const along = (vx * dx + vz * dz) / len, across = Math.abs(vx * dz - vz * dx) / len;
    return along >= -5 && along <= len + 5 && across <= hw - 0.3;
  };
  const press = (x: number, z: number, road: number, quad: number) => {
    if (x < 0 || z < 0 || x > width || z > depth) return;
    const fx = Math.min(1, Math.max(0, x / width)) * gridRes;
    const fz = Math.min(1, Math.max(0, z / depth)) * gridRes;
    const j = Math.min(gridRes - 1, Math.floor(fx)), i = Math.min(gridRes - 1, Math.floor(fz));
    const u = fx - j, v = fz - i;
    const a = i * side + j, b = a + 1, c = a + side, d = c + 1;
    const tri = u + v <= 1 ? [a, b, c] : [d, b, c];
    // How much each corner counts at (x, z).
    const weight = u + v <= 1 ? [1 - u - v, u, v] : [u + v - 1, 1 - v, 1 - u];
    const height = () => tri.reduce((sum, vi, k) => sum + gridHeights[vi] * weight[k], 0);
    // Never under the road itself: with all three corners there, so is the
    // ground anywhere in the triangle. Pressed by `need` alone, a vertex
    // beside the tile's edge went on down after the edge vertices that
    // cannot move: a 4 m pit beside every road along an edge. So the drop is
    // shared out among the corners that can still go down, by how much each
    // counts here, as often as it takes: with one corner at the road
    // already, the others went down by the little the point was over it, and
    // a path into an underpass was lifted 30 cm onto the bank at its portal.
    // Except beside the edge: with a corner on it that stays up, a corner
    // under the road's lane goes on down, to `EDGE_DITCH` below it, so the
    // slope up to the edge's vertex passes under the kerb. Nothing of that
    // shows: it is under the road, and outside the kerb the ground climbs
    // straight from it to the edge (see the floor in `solveRoad`).
    const onEdge = tri.map(vi => {
      const row = Math.floor(vi / side), col = vi % side;
      return row === 0 || col === 0 || row === gridRes || col === gridRes;
    });
    const beside = onEdge.some(e => e);
    const lowest = tri.map(vi => road - (beside && underLane(vi, quad) ? EDGE_DITCH : 0));
    for (let round = 0; round < 3; round++) {
      const need = height() - road;
      if (need <= 1e-4) return;
      const movable = tri.map((vi, k) =>
        !onEdge[k] && gridHeights[vi] > lowest[k] && gridHeights[vi] > before[vi] - PRESS_MOST ? weight[k] : 0);
      const share = movable.reduce((sum, w) => sum + w, 0);
      if (share < 1e-6) return;
      tri.forEach((vi, k) => {
        if (movable[k] === 0) return;
        gridHeights[vi] = Math.max(before[vi] - PRESS_MOST, gridHeights[vi] - need / share, lowest[k]);
        changed = true;
      });
    }
  };
  for (let pass = 0; pass < 2; pass++) {
    for (let q = 0; q < quads; q++) {
      const a = roads.indices[q * 6];
      if (roads.bridges[a] !== 0) continue;
      const style = roads.attributes[a * 4 + 2];
      if (Number.isNaN(heights[a]) || Number.isNaN(heights[a + 2])) continue;
      const clear = Math.min(lift(style), UNDER_ROAD);
      for (const t of ACROSS) {
        for (const [l, r] of [[a, a + 1], [a + 2, a + 3]] as const) {
          press(p[l * 2] + (p[r * 2] - p[l * 2]) * t, p[l * 2 + 1] + (p[r * 2 + 1] - p[l * 2 + 1]) * t,
            heights[l] + (heights[r] - heights[l]) * t - clear, a);
        }
        // Halfway along the quad.
        const lx = (p[a * 2] + p[(a + 2) * 2]) / 2, lz = (p[a * 2 + 1] + p[(a + 2) * 2 + 1]) / 2;
        const rx = (p[(a + 1) * 2] + p[(a + 3) * 2]) / 2, rz = (p[(a + 1) * 2 + 1] + p[(a + 3) * 2 + 1]) / 2;
        const hl = (heights[a] + heights[a + 2]) / 2, hr = (heights[a + 1] + heights[a + 3]) / 2;
        press(lx + (rx - lx) * t, lz + (rz - lz) * t, hl + (hr - hl) * t - clear, a);
      }
    }
  }
  if (changed) refreshTerrainGeometry(mesh);
  return changed;
}

/**
 * Lift a road wherever the reshaped ground still pokes through it: sampled
 * across the whole width — kerbs, quarters and centre — at every section and
 * halfway to the next, where the terrain's triangles are larger than the
 * corridor could fully reshape. `heights` are the ribbon's final Y per vertex,
 * edited in place; a section is lifted as a whole, so it stays level.
 */
export function clearRoadOfGround(
  roads: RoadRibbons,
  heights: Float32Array,
  ground: (x: number, z: number) => number,
  lift: (style: number) => number,
  /**
   * The tile's extent. Within a cell of its edge the ground is the bank the
   * edge's vertices hold up, which the design let stand into the road as far
   * as it may (`EDGE_WEDGE`, `EDGE_LIFT` in `solveRoad`): no lift over that,
   * which was a hump a section long.
   */
  width = Infinity,
  depth = Infinity,
): void {
  const quads = Math.floor(roads.indices.length / 6);
  const p = roads.positions;
  const ACROSS = [0, 0.25, 0.5, 0.75, 1];
  const bounded = Number.isFinite(width) && Number.isFinite(depth);
  const besideEdge = (x: number, z: number) => bounded && Math.min(x, z, width - x, depth - z) < EDGE_CELL;
  const centreKey = (v: number) => `${((p[v * 2] + p[(v + 1) * 2]) / 2).toFixed(1)},${((p[v * 2 + 1] + p[(v + 1) * 2 + 1]) / 2).toFixed(1)}`;
  const portalFloors = new Map<string, number>();
  for (let q = 0; q < quads; q++) {
    const a = roads.indices[q * 6];
    if (roads.bridges[a] !== TUNNEL) continue;
    if (q === 0 || roads.indices[(q - 1) * 6] + 2 !== a || roads.bridges[roads.indices[(q - 1) * 6]] !== TUNNEL) portalFloors.set(centreKey(a), heights[a]);
    if (q + 1 === quads || roads.indices[(q + 1) * 6] !== a + 2 || roads.bridges[roads.indices[(q + 1) * 6]] !== TUNNEL) portalFloors.set(centreKey(a + 2), heights[a + 2]);
  }
  const atPortal = (v: number) => {
    const floor = portalFloors.get(centreKey(v));
    return floor !== undefined && Math.abs(heights[v] - floor) < 1;
  };
  // How far the ground under the line from vertex u to vertex w (sampled
  // across) rises above the road there, plus the clearance.
  const intrusion = (u: number, w: number, min: number) => {
    let worst = -Infinity;
    for (const t of ACROSS) {
      const x = p[u * 2] + (p[w * 2] - p[u * 2]) * t;
      const z = p[u * 2 + 1] + (p[w * 2 + 1] - p[u * 2 + 1]) * t;
      if (besideEdge(x, z)) continue;
      const road = heights[u] + (heights[w] - heights[u]) * t;
      worst = Math.max(worst, ground(x, z) + min - road);
    }
    return worst;
  };
  for (let q = 0; q < quads; q++) {
    const a = roads.indices[q * 6];
    if (roads.bridges[a] !== 0) continue;
    // Nor a way through an underpass: the ground between its trough's walls
    // is not drawn (`buildTroughs`). Lifted over the edge's ground there, the
    // motorway's bank either side of it, one by the tile's edge rose 0.8 m
    // in a hump under the deck.
    if (roads.underpasses?.[a] === 1) continue;
    const style = roads.attributes[a * 4 + 2];
    const min = Math.min(lift(style), UNDER_ROAD);
    for (const left of [a, a + 2]) {
      // The mouth cuts this bank away in the terrain shader. Lifting the
      // approach back onto it breaks the already matched tunnel floor.
      if (atPortal(left)) continue;
      const rise = intrusion(left, left + 1, min);
      if (rise > 0) { heights[left] += rise; heights[left + 1] += rise; }
    }
    // Halfway between the two sections, across the road.
    let worst = -Infinity;
    for (const t of ACROSS) {
      const lx = (p[a * 2] + p[(a + 2) * 2]) / 2, lz = (p[a * 2 + 1] + p[(a + 2) * 2 + 1]) / 2;
      const rx = (p[(a + 1) * 2] + p[(a + 3) * 2]) / 2, rz = (p[(a + 1) * 2 + 1] + p[(a + 3) * 2 + 1]) / 2;
      const road = ((heights[a] + heights[a + 2]) / 2) * (1 - t) + ((heights[a + 1] + heights[a + 3]) / 2) * t;
      const x = lx + (rx - lx) * t, z = lz + (rz - lz) * t;
      if (besideEdge(x, z)) continue;
      worst = Math.max(worst, ground(x, z) + min - road);
    }
    if (worst > 0 && !atPortal(a) && !atPortal(a + 2)) for (const v of [a, a + 1, a + 2, a + 3]) heights[v] += worst;
  }
}

/**
 * Embankment faces down the sides of raised roads: from each kerb, sloping out
 * and down to meet the ground, wherever the road stands proud of it. Without
 * them a road on fill is a ribbon floating over a gap you can see under.
 *
 * Returned in the bridge buffers' format — x,y,z, linear RGB, indices — so it
 * draws with the bridge structure: one more mesh's worth of nothing.
 */
export function roadEmbankments(
  roads: RoadRibbons,
  placed: Float32Array,
  ground: (x: number, z: number) => number,
  colour: [number, number, number] | ((x: number, z: number) => [number, number, number]),
  /**
   * Where no face comes down, for one from `top` at the kerb to `bottom` at
   * its foot: a trough's walls hold the bank there, and a face down from a
   * road over a tunnel's portal crossed the tube.
   */
  keepOut?: (x: number, z: number, bottom: number, top: number) => boolean,
): { positions: Float32Array; colors: Float32Array; indices: Uint32Array } {
  const positions: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const quads = Math.floor(roads.indices.length / 6);
  const p = roads.positions;
  // Where the face meets the ground, for kerb vertex v: out along the section
  // by the drop (a 1:1 slope), and down a little into the ground.
  const foot = (v: number, other: number): [number, number, number, number] => {
    const x = p[v * 2], z = p[v * 2 + 1];
    const ox = x - p[other * 2], oz = z - p[other * 2 + 1];
    const len = Math.hypot(ox, oz) || 1;
    const top = placed[v * 3 + 1] - 0.03;
    const drop = Math.max(0, top - ground(x, z));
    const reach = Math.min(8, drop);
    const fx = x + (ox / len) * reach, fz = z + (oz / len) * reach;
    return [fx, Math.min(top, ground(fx, fz)) - 0.25, fz, drop];
  };
  for (let q = 0; q < quads; q++) {
    const a = roads.indices[q * 6];
    if (roads.bridges[a] !== 0) continue;
    // Left kerb runs a -> a+2, right kerb a+1 -> a+3.
    for (const [v0, v1, o0, o1] of [[a, a + 2, a + 1, a + 3], [a + 3, a + 1, a + 2, a]] as const) {
      const f0 = foot(v0, o0), f1 = foot(v1, o1);
      if (keepOut !== undefined && [[v0, f0], [v1, f1]].some(([v, f]) => {
        const kerb = placed[(v as number) * 3 + 1], [fx, fy, fz] = f as number[];
        const mx = (p[(v as number) * 2] + fx) / 2, mz = (p[(v as number) * 2 + 1] + fz) / 2;
        return keepOut(fx, fz, fy, kerb) || keepOut(mx, mz, fy, kerb);
      })) continue;
      // Only where the road clearly stands proud: below this the reshaped
      // ground already meets the kerb, and a face there is triangles for nothing.
      if (Math.max(f0[3], f1[3]) < EMBANKMENT_MIN_DROP) continue;
      const base = positions.length / 3;
      positions.push(
        p[v0 * 2], placed[v0 * 3 + 1] - 0.03, p[v0 * 2 + 1],
        p[v1 * 2], placed[v1 * 3 + 1] - 0.03, p[v1 * 2 + 1],
        f1[0], f1[1], f1[2],
        f0[0], f0[1], f0[2],
      );
      const tone = typeof colour === 'function' ? colour((p[v0 * 2] + p[v1 * 2]) / 2, (p[v0 * 2 + 1] + p[v1 * 2 + 1]) / 2) : colour;
      for (let i = 0; i < 4; i++) colors.push(...tone);
      indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
  }
  return { positions: new Float32Array(positions), colors: new Float32Array(colors), indices: new Uint32Array(indices) };
}
